import { apiErrorMessage, detailOr } from "@/lib/apiErrorMessage";
/**
 * Super Admin Portal — MiseOne platform administration.
 *
 * Scope (Prompt 20):
 *   - Platform overview, company / restaurant / user / subscription
 *     management, platform reports, cross-entity search, notifications.
 *   - Only users with role === "super_admin" reach this page; the route
 *     enforces it on top of the existing `<ProtectedRoute>` gate, and the
 *     backend re-checks via `require_super_admin` on every endpoint.
 *   - Reuses the existing design system (PageHeader, kop-card, StatusBadge).
 *   - All admin endpoints live at `/api/admin/*`.
 */
import i18n from "@/i18n";
import { useCallback, useEffect, useState } from "react";
import { useLatest } from "@/hooks/useLatest";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    NewRestaurantDialog, NewUserDialog, NewPlanDialog, NewSubscriptionDialog,
} from "@/pages/admin/AdminCreateDialogs";
import AdminConfiguration from "@/pages/AdminConfiguration";
import LegalTab from "@/pages/admin/LegalTab";
import {
    LayoutGrid, Building2, Store, Users, CreditCard, BarChart3,
    BellRing, Search, Lock, Unlock, KeyRound, Pause, Play,
    Activity, Package, LifeBuoy, X, MessageSquare, Edit2, Plus, Check,
    Sparkles, Bug, GraduationCap, Rocket, TrendingUp, Settings as Cog,
    FileText, Receipt, Download, Mail, Copy as CopyIcon, Trash2,
    SlidersHorizontal as SettingsIcon,
    ShieldCheck,
} from "lucide-react";

// The Super Admin console is the MiseOne internal operations page.
// It groups its 15 tabs into three functional areas so the platform owner
// can visually navigate: Customer Management, Commercial, Platform.
const TAB_GROUPS = [
    {
        get label() { return i18n.t("admin.l.Customer_Management", "Customer Management"); },
        tabs: [
            { key: "dashboard", get label() { return i18n.t("admin.l.Dashboard", "Dashboard"); }, icon: LayoutGrid },
            { key: "lifecycle", get label() { return i18n.t("admin.l.Lifecycle", "Lifecycle"); }, icon: Activity },
            { key: "companies", get label() { return i18n.t("admin.l.Companies", "Companies"); }, icon: Building2 },
            { key: "restaurants", get label() { return i18n.t("admin.l.Restaurants", "Restaurants"); }, icon: Store },
            { key: "users", get label() { return i18n.t("admin.l.Users", "Users"); }, icon: Users },
            { key: "support", get label() { return i18n.t("admin.l.Support", "Support"); }, icon: LifeBuoy },
        ],
    },
    {
        get label() { return i18n.t("admin.l.Commercial", "Commercial"); },
        tabs: [
            { key: "commercial", get label() { return i18n.t("admin.l.Sales_Overview", "Sales Overview"); }, icon: TrendingUp },
            { key: "plans", get label() { return i18n.t("admin.l.Plans", "Plans"); }, icon: Package },
            { key: "subscriptions", get label() { return i18n.t("admin.l.Subscriptions", "Subscriptions"); }, icon: CreditCard },
            { key: "invoices", get label() { return i18n.t("admin.l.Invoices", "Invoices"); }, icon: FileText },
            { key: "billing_settings", get label() { return i18n.t("admin.l.Billing_Settings", "Billing Settings"); }, icon: Receipt },
            { key: "go_live", get label() { return i18n.t("admin.l.Go_Live", "Go-Live"); }, icon: Rocket },
        ],
    },
    {
        get label() { return i18n.t("admin.l.Platform", "Platform"); },
        tabs: [
            { key: "features", get label() { return i18n.t("admin.l.Feature_Requests", "Feature Requests"); }, icon: Sparkles },
            { key: "bugs", get label() { return i18n.t("admin.l.Bug_Reports", "Bug Reports"); }, icon: Bug },
            { key: "training", get label() { return i18n.t("admin.l.Training", "Training"); }, icon: GraduationCap },
            { key: "reports", get label() { return i18n.t("admin.l.Reports", "Reports"); }, icon: BarChart3 },
            { key: "notifications", get label() { return i18n.t("admin.l.Alerts", "Alerts"); }, icon: BellRing },
            { key: "demo", get label() { return i18n.t("admin.l.Sales_Demo", "Sales Demo"); }, icon: Rocket },
            { key: "configuration", get label() { return i18n.t("admin.l.Configuration", "Configuration"); }, icon: SettingsIcon },
            { key: "legal", get label() { return i18n.t("admin.l.Legal_Documents", "Legal Documents"); }, icon: ShieldCheck },
            { key: "settings", get label() { return i18n.t("admin.l.Settings", "Settings"); }, icon: Cog },
        ],
    },
];
const TABS = TAB_GROUPS.flatMap((g) => g.tabs);

// Per-tab page header content (rendered instead of the old horizontal tab bar).
const TAB_META = {
    dashboard:     { get title() { return i18n.t("admin.l.Dashboard", "Dashboard"); },         get subtitle() { return i18n.t("admin.l.Business_snapshot_who_is_active_who_is_in_trial_what_needs_attention_t", "Business snapshot — who is active, who is in trial, what needs attention today."); } },
    companies:     { get title() { return i18n.t("admin.l.Companies", "Companies"); },         get subtitle() { return i18n.t("admin.l.The_center_of_the_platform_Each_company_holds_restaurants_users_subscr", "The center of the platform. Each company holds restaurants, users, subscription and lifecycle."); } },
    lifecycle:     { get title() { return i18n.t("admin.l.Customer_Lifecycle", "Customer Lifecycle"); },get subtitle() { return i18n.t("admin.l.Track_every_customer_from_lead_through_active_follow_ups_and_health", "Track every customer from lead through active, follow-ups and health."); } },
    support:       { get title() { return i18n.t("admin.l.Support_Tickets", "Support Tickets"); },   get subtitle() { return i18n.t("admin.l.Customer_support_notes_and_follow_ups", "Customer support notes and follow-ups."); } },
    features:      { get title() { return i18n.t("admin.l.Feature_Requests", "Feature Requests"); },  get subtitle() { return i18n.t("admin.l.Ideas_customers_ask_for_Prioritise_for_the_roadmap", "Ideas customers ask for. Prioritise for the roadmap."); } },
    bugs:          { get title() { return i18n.t("admin.l.Bug_Reports", "Bug Reports"); },       get subtitle() { return i18n.t("admin.l.Defects_reported_by_customers_Severity_and_resolution", "Defects reported by customers. Severity and resolution."); } },
    training:      { get title() { return i18n.t("admin.l.Training", "Training"); },          get subtitle() { return i18n.t("admin.l.Onboarding_calls_and_refresher_sessions_per_restaurant", "Onboarding calls and refresher sessions per restaurant."); } },
    commercial:    { get title() { return i18n.t("admin.l.Sales_Overview", "Sales Overview"); },    get subtitle() { return i18n.t("admin.l.Pipeline_snapshot_per_lifecycle_stage", "Pipeline snapshot per lifecycle stage."); } },
    plans:         { get title() { return i18n.t("admin.l.Plans", "Plans"); },             get subtitle() { return i18n.t("admin.l.Subscription_plans_customers_can_select", "Subscription plans customers can select."); } },
    subscriptions: { get title() { return i18n.t("admin.l.Subscriptions", "Subscriptions"); },     get subtitle() { return i18n.t("admin.l.One_subscription_per_company_Statuses_tracked_here", "One subscription per company. Statuses tracked here."); } },
    reports:       { get title() { return i18n.t("admin.l.Platform_Reports", "Platform Reports"); },  get subtitle() { return i18n.t("admin.l.SaaS_business_metrics_growth_health_support_volume", "SaaS business metrics — growth, health, support volume."); } },
    settings:      { get title() { return i18n.t("admin.l.Platform_Settings", "Platform Settings"); }, get subtitle() { return i18n.t("admin.l.MiseOne_business_settings_and_platform_tools", "MiseOne business settings and platform tools."); } },
    configuration: { get title() { return i18n.t("admin.l.MiseOne_Configuration", "MiseOne Configuration"); }, get subtitle() { return i18n.t("admin.l.Platform_Default_per_Company_and_per_Restaurant_overrides_Super_Admin_", "Platform Default, per-Company and per-Restaurant overrides. Super Admin only."); } },
    legal:         { get title() { return i18n.t("admin.l.Legal_Documents", "Legal Documents"); },   get subtitle() { return i18n.t("admin.legalSubtitle", "Master Service Agreement, Privacy, DPA, SLA, Cookie Policy and Terms. Publish, edit, and monitor per-company acceptance."); } },
    // Legacy tabs kept for backward-compat deep links; not primary nav.
    restaurants:   { get title() { return i18n.t("admin.l.Restaurants", "Restaurants"); },       get subtitle() { return i18n.t("admin.l.Cross_company_restaurants_list", "Cross-company restaurants list."); } },
    users:         { get title() { return i18n.t("admin.l.Users", "Users"); },             get subtitle() { return i18n.t("admin.l.Cross_company_users_list", "Cross-company users list."); } },
    go_live:       { get title() { return i18n.t("admin.l.Go_Live_Readiness", "Go-Live Readiness"); }, get subtitle() { return i18n.t("admin.l.Onboarding_checklist_per_company", "Onboarding checklist per company."); } },
    notifications: { get title() { return i18n.t("admin.l.Platform_Alerts", "Platform Alerts"); },   get subtitle() { return i18n.t("admin.l.Trials_ending_soon_and_other_platform_alerts", "Trials ending soon and other platform alerts."); } },
    demo:          { get title() { return i18n.t("admin.l.Sales_Demo_Tenant", "Sales Demo Tenant"); }, get subtitle() { return i18n.t("admin.l.Isolated_demo_company_for_Product_Owner_sales_presentations_Excluded_f", "Isolated demo company for Product Owner sales presentations. Excluded from all real KPIs."); } },
};

const LIFECYCLE_STAGES = [
    "lead", "demo_scheduled", "pilot", "trial",
    "active_customer", "pending_payment",
    "suspended", "cancelled", "archived",
];
const LIFECYCLE_LABEL = {
    get lead() { return i18n.t("admin.lc.lead", "Lead"); }, get demo_scheduled() { return i18n.t("admin.lc.demo_scheduled", "Demo Scheduled"); }, get pilot() { return i18n.t("admin.lc.pilot", "Pilot"); },
    get trial() { return i18n.t("admin.lc.trial", "Trial"); }, get active_customer() { return i18n.t("admin.lc.active_customer", "Active Customer"); },
    get pending_payment() { return i18n.t("admin.lc.pending_payment", "Pending Payment"); }, get suspended() { return i18n.t("admin.l.Suspended", "Suspended"); },
    get cancelled() { return i18n.t("admin.l.Cancelled", "Cancelled"); }, get archived() { return i18n.t("admin.l.Archived", "Archived"); },
};
const LIFECYCLE_TONE = {
    lead: "pending", demo_scheduled: "pending", pilot: "pending",
    trial: "pending", active_customer: "completed",
    pending_payment: "action_required", suspended: "action_required",
    cancelled: "action_required", archived: "low",
};
const SUBSCRIPTION_STATUSES = [
    "trial", "active", "pending_payment",
    "suspended", "cancelled", "expired", "manual_invoice",
];
const SUPPORT_PRIORITIES = ["low", "medium", "high"];
const SUPPORT_STATUSES = ["open", "in_progress", "waiting_customer", "resolved", "closed"];

export default function AdminPortal() {
    const { user } = useApp();
    const navigate = useNavigate();
    const location = useLocation();
    const [tab, setTab] = useState(() => {
        const p = new URLSearchParams(window.location.search).get("tab");
        return TABS.find((t) => t.key === p) ? p : "dashboard";
    });
    // "New" dialog control — driven by ?new=1 on any of the CRUD tabs.
    const [newDialog, setNewDialog] = useState(null);
    // Reload trigger: bump this to force list re-fetches inside tabs.
    const [reloadTick, setReloadTick] = useState(0);
    const tabRef = useLatest(tab);

    // React to URL changes triggered by <navigate> (Topbar +New, Wizard
    // post-create redirect, Sidebar clicks). Keep state authoritative on
    // the URL so deep-links work identically to in-app navigation.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const nextTab = params.get("tab");
        const tab = tabRef.current;
        if (nextTab && TABS.find((t) => t.key === nextTab) && nextTab !== tab) {
            setTab(nextTab);
        }
        // ?new=1 → open the matching create dialog for the current tab.
        if (params.get("new") === "1") {
            const map = {
                restaurants: "restaurant",
                users: "user",
                plans: "plan",
                subscriptions: "subscription",
            };
            const key = map[nextTab || tab];
            if (key) setNewDialog(key);
            // Clear ?new=1 so refresh doesn't reopen the dialog.
            const url = new URL(window.location.href);
            url.searchParams.delete("new");
            window.history.replaceState({}, "", url);
        }
    }, [location.search, tabRef]);

    // Keep the URL in sync when the user clicks a Sidebar item that only
    // toggles `tab` locally (defensive — mostly a no-op).
    useEffect(() => {
        const url = new URL(window.location.href);
        if (url.searchParams.get("tab") !== tab) {
            url.searchParams.set("tab", tab);
            window.history.replaceState({}, "", url);
        }
    }, [tab]);

    useEffect(() => {
        if (user && user.role !== "super_admin") navigate("/workflow", { replace: true });
    }, [user, navigate]);

    if (!user || user.role !== "super_admin") return null;

    // Human-readable title/subtitle for the current tab.
    const meta = TAB_META[tab] || TAB_META.dashboard;

    return (
        <div className="px-6 lg:px-12 py-10 max-w-7xl mx-auto" data-testid="admin-portal">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-neutral-500">
                {i18n.t("admin.l.MiseOne_Platform_Console", "MiseOne · Platform Console")}
            </div>
            <PageHeader showTitle title={meta.title} subtitle={meta.subtitle} />

            <div className="mt-8">
                {tab === "dashboard" && <AdminDashboard />}
                {tab === "lifecycle" && <LifecycleTab />}
                {tab === "companies" && <CompaniesTab />}
                {tab === "plans" && <PlansTab />}
                {tab === "subscriptions" && <SubscriptionsTab />}
                {tab === "support" && <SupportTab />}
                {tab === "commercial" && <CommercialTab />}
                {tab === "features" && <FeatureRequestsTab />}
                {tab === "bugs" && <BugReportsTab />}
                {tab === "training" && <TrainingTab />}
                {tab === "go_live" && <GoLiveTab />}
                {tab === "restaurants" && <RestaurantsTab />}
                {tab === "users" && <UsersTab key={`users-${reloadTick}`} />}
                {tab === "reports" && <ReportsTab />}
                {tab === "invoices" && <InvoicesTab />}
                {tab === "billing_settings" && <PlatformBillingSettingsTab />}
                {tab === "notifications" && <NotificationsTab />}
                {tab === "demo" && <DemoTab />}
                {tab === "configuration" && <AdminConfiguration />}
                {tab === "legal" && <LegalTab />}
                {tab === "settings" && <PlatformSettingsTab />}
            </div>

            {newDialog === "restaurant" && (
                <NewRestaurantDialog
                    onClose={() => setNewDialog(null)}
                    onCreated={() => { setNewDialog(null); setReloadTick((n) => n + 1); }}
                />
            )}
            {newDialog === "user" && (
                <NewUserDialog
                    onClose={() => setNewDialog(null)}
                    onCreated={() => { setNewDialog(null); setReloadTick((n) => n + 1); }}
                />
            )}
            {newDialog === "plan" && (
                <NewPlanDialog
                    onClose={() => setNewDialog(null)}
                    onCreated={() => { setNewDialog(null); setReloadTick((n) => n + 1); }}
                />
            )}
            {newDialog === "subscription" && (
                <NewSubscriptionDialog
                    onClose={() => setNewDialog(null)}
                    onCreated={() => { setNewDialog(null); setReloadTick((n) => n + 1); }}
                />
            )}
        </div>
    );
}

// ============================================================ Dashboard
function AdminDashboard() {
    const [data, setData] = useState(null);
    const [alerts, setAlerts] = useState({open_bugs: 0, open_features: 0});
    const navigate = useNavigate();
    useEffect(() => {
        api.get("/admin/dashboard").then(({ data }) => setData(data));
        // Best-effort counts for the "Attention" card.
        Promise.all([
            api.get("/admin/bug-reports").catch(() => ({data: []})),
            api.get("/admin/feature-requests").catch(() => ({data: []})),
        ]).then(([b, f]) => setAlerts({
            open_bugs: (b.data || []).filter((x) => x.status !== "resolved" && x.status !== "closed").length,
            open_features: (f.data || []).filter((x) => x.status !== "resolved" && x.status !== "closed" && x.status !== "shipped").length,
        }));
    }, []);
    if (!data) return <div className="text-sm text-neutral-500">{i18n.t("admin.l.Loading", "Loading…")}</div>;
    const c = data.companies;
    const items = [
        { get label() { return i18n.t("admin.l.Total_companies", "Total companies"); }, value: c.total, key: "total" },
        { get label() { return i18n.t("admin.l.Active_customers", "Active customers"); }, value: data.subscription_status_counts?.active || 0, key: "active" },
        { get label() { return i18n.t("admin.l.Trials", "Trials"); }, value: data.subscription_status_counts?.trial || 0, key: "trial" },
        { get label() { return i18n.t("admin.l.Trials_ending_in_7d", "Trials ending in 7d"); }, value: data.trials_ending_soon || 0, key: "trials_soon" },
        { get label() { return i18n.t("admin.l.Suspended", "Suspended"); }, value: data.subscription_status_counts?.suspended || 0, key: "suspended" },
        { get label() { return i18n.t("admin.l.Open_support", "Open support"); }, value: data.open_support_notes || 0, key: "open_support" },
        { get label() { return i18n.t("admin.l.Open_bugs", "Open bugs"); }, value: alerts.open_bugs, key: "open_bugs" },
        { get label() { return i18n.t("admin.l.Open_features", "Open features"); }, value: alerts.open_features, key: "open_features" },
        { get label() { return i18n.t("admin.l.Restaurants", "Restaurants"); }, value: data.restaurants_total, key: "rest" },
        { get label() { return i18n.t("admin.l.New_this_month", "New this month"); }, value: data.new_companies_this_month, key: "new" },
    ];
    const lifecycle = data.lifecycle_counts || {};
    const planDist = data.plan_distribution || {};
    // Attention list — one-liners the owner should see today.
    const attention = [];
    if ((data.trials_ending_soon || 0) > 0) attention.push({
        key: "trials_soon",
        text: `${data.trials_ending_soon} trial${data.trials_ending_soon>1?"s":""} ending in 7 days`,
        get cta() { return i18n.t("admin.l.Review", "Review"); }, to: "/admin?tab=subscriptions",
    });
    if ((data.open_support_notes || 0) > 0) attention.push({
        key: "open_support",
        text: `${data.open_support_notes} open support note${data.open_support_notes>1?"s":""}`,
        get cta() { return i18n.t("admin.l.Open", "Open"); }, to: "/admin?tab=support",
    });
    if ((data.subscription_status_counts?.pending_payment || 0) > 0) attention.push({
        key: "pending_payment",
        text: `${data.subscription_status_counts.pending_payment} customer${data.subscription_status_counts.pending_payment>1?"s":""} with pending payment`,
        get cta() { return i18n.t("admin.l.Follow_up", "Follow-up"); }, to: "/admin?tab=subscriptions",
    });
    if (alerts.open_bugs > 0) attention.push({
        key: "bugs",
        text: `${alerts.open_bugs} open bug report${alerts.open_bugs>1?"s":""}`,
        get cta() { return i18n.t("admin.l.Review", "Review"); }, to: "/admin?tab=bugs",
    });
    return (
        <div className="space-y-6" data-testid="admin-dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {items.map((it) => (
                    <div key={it.key} className="kop-card p-5" data-testid={`admin-metric-${it.key}`}>
                        <div className="text-xs uppercase tracking-wide text-neutral-500">{it.label}</div>
                        <div className="mt-1 font-display text-3xl font-extrabold">{it.value}</div>
                    </div>
                ))}
            </div>

            <div className="kop-card p-5" data-testid="admin-attention">
                <div className="flex items-baseline justify-between mb-3">
                    <div className="text-xs uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Needs_attention", "Needs attention")}</div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-400">
                        {i18n.t("admin.l.Platform_alerts", "Platform alerts")}
                    </div>
                </div>
                {attention.length === 0 ? (
                    <div className="text-sm text-neutral-500 italic">{i18n.t("admin.l.All_quiet_Nothing_urgent_right_now", "All quiet. Nothing urgent right now.")}</div>
                ) : (
                    <ul className="divide-y divide-neutral-100">
                        {attention.map((a) => (
                            <li key={a.key} data-testid={`admin-attention-${a.key}`}
                                className="py-2 flex items-center justify-between">
                                <span className="text-sm">{a.text}</span>
                                <button
                                    onClick={() => navigate(a.to)}
                                    className="text-xs font-semibold text-neutral-900 underline underline-offset-2 hover:no-underline">
                                    {a.cta}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="kop-card p-5" data-testid="admin-lifecycle-distribution">
                    <div className="text-xs uppercase tracking-wide text-neutral-500 mb-3">{i18n.t("admin.l.Lifecycle_distribution", "Lifecycle distribution")}</div>
                    {LIFECYCLE_STAGES.map((k) => (
                        <div key={k} className="flex items-center justify-between py-1 text-sm border-b border-neutral-100 last:border-0">
                            <span>{LIFECYCLE_LABEL[k]}</span>
                            <span className="font-semibold">{lifecycle[k] || 0}</span>
                        </div>
                    ))}
                </div>
                <div className="kop-card p-5" data-testid="admin-plan-distribution">
                    <div className="text-xs uppercase tracking-wide text-neutral-500 mb-3">{i18n.t("admin.l.Plan_distribution", "Plan distribution")}</div>
                    {Object.keys(planDist).length === 0 ? (
                        <div className="text-sm text-neutral-500 italic">{i18n.t("admin.l.No_subscriptions_yet", "No subscriptions yet.")}</div>
                    ) : Object.entries(planDist).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between py-1 text-sm border-b border-neutral-100 last:border-0">
                            <span className="capitalize">{k}</span><span className="font-semibold">{v}</span>
                        </div>
                    ))}
                </div>
            </div>
            <div className="kop-card p-5 flex items-center justify-between"
                 data-testid="admin-system-status">
                <div>
                    <div className="text-xs uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.System_status", "System status")}</div>
                    <div className="mt-1 font-semibold text-emerald-700 capitalize">{data.system_status}</div>
                </div>
                <StatusBadge status="completed">{i18n.t("admin.l.Operational", "Operational")}</StatusBadge>
            </div>
        </div>
    );
}

// ============================================================ Companies
function CompaniesTab() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);
    const load = useCallback(() => api.get("/admin/companies", { params: q ? { q } : {} }).then(({ data }) => setRows(data)), [q]);
    useEffect(() => { load(); }, [load]);

    const toggleSuspend = async (c) => {
        const next = c.status === "suspended" ? "active" : "suspended";
        try {
            await api.patch(`/admin/companies/${c.id}`, { status: next });
            toast.success(next === "active" ? i18n.t("admin.companyActive", "Company active") : i18n.t("admin.companySuspended", "Company suspended"));
            load();
        } catch { toast.error(i18n.t("admin.l.Action_failed", "Action failed")); }
    };

    return (
        <div data-testid="admin-companies">
            <div className="mb-4 flex items-center gap-2 max-w-md">
                <Search className="h-4 w-4 text-neutral-400" />
                <input
                    data-testid="admin-companies-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={i18n.t("admin.l.Search_companies", "Search companies")}
                    className="h-9 px-3 rounded-md border border-neutral-300 flex-1 text-sm"
                />
            </div>
            <div className="kop-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                        <tr>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Company", "Company")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Country", "Country")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Status", "Status")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Plan", "Plan")}</th>
                            <th className="text-right px-4 py-2">{i18n.t("admin.l.Restaurants", "Restaurants")}</th>
                            <th className="text-right px-4 py-2">{i18n.t("admin.l.Users", "Users")}</th>
                            <th className="text-right px-4 py-2">{i18n.t("admin.l.Actions", "Actions")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan="7" className="px-4 py-6 text-center text-neutral-500">{i18n.t("admin.l.No_companies", "No companies.")}</td></tr>
                        )}
                        {rows.map((c) => (
                            <tr key={c.id} className="border-t border-neutral-200" data-testid={`admin-company-row-${c.id}`}>
                                <td className="px-4 py-2 font-medium">{c.name}</td>
                                <td className="px-4 py-2 text-neutral-600">{c.country || "—"}</td>
                                <td className="px-4 py-2">
                                    <StatusBadge status={c.status === "suspended" ? "action_required" : "completed"}>
                                        {c.status || "active"}
                                    </StatusBadge>
                                </td>
                                <td className="px-4 py-2 capitalize">{c.subscription?.plan || "—"}</td>
                                <td className="px-4 py-2 text-right">{c.restaurant_count}</td>
                                <td className="px-4 py-2 text-right">{c.user_count}</td>
                                <td className="px-4 py-2 text-right space-x-1">
                                    <button
                                        data-testid={`admin-company-open-${c.id}`}
                                        onClick={() => navigate(`/admin/companies/${c.id}`)}
                                        className="h-8 px-3 inline-flex items-center gap-1.5 rounded-md bg-neutral-900 text-white text-xs font-semibold hover:bg-neutral-800"
                                    >
                                        Manage
                                    </button>
                                    <button
                                        data-testid={`admin-company-toggle-${c.id}`}
                                        onClick={() => toggleSuspend(c)}
                                        className="h-8 px-3 inline-flex items-center gap-1.5 rounded-md border border-neutral-300 text-xs font-semibold hover:bg-neutral-50"
                                    >
                                        {c.status === "suspended"
                                            ? (<><Play className="h-3 w-3" /> {i18n.t("admin.l.Reactivate", "Reactivate")}</>)
                                            : (<><Pause className="h-3 w-3" /> {i18n.t("admin.l.Suspend", "Suspend")}</>)}
                                    </button>
                                    <button
                                        data-testid={`admin-company-delete-${c.id}`}
                                        onClick={() => setDeleteTarget(c)}
                                        title={i18n.t("admin.l.Delete_company", "Delete company")}
                                        className="h-8 px-3 inline-flex items-center gap-1.5 rounded-md border border-red-200 text-xs font-semibold text-red-700 hover:bg-red-50"
                                    >
                                        <Trash2 className="h-3 w-3" /> {i18n.t("admin.l.Delete", "Delete")}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <DeleteCompanyDialog target={deleteTarget}
                                   onClose={() => setDeleteTarget(null)}
                                   onDeleted={() => { setDeleteTarget(null); load(); }} />
        </div>
    );
}

function DeleteCompanyDialog({ target, onClose, onDeleted }) {
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { setConfirm(""); }, [target?.id]);
    if (!target) return null;
    const canConfirm = confirm.trim() === (target.name || "").trim();
    const doDelete = async () => {
        setBusy(true);
        try {
            const { data } = await api.delete(
                `/admin/companies/${target.id}`,
                { params: { confirm } });
            toast.success(`Deleted “${target.name}” · ${
                Object.values(data.deleted || {}).reduce((a, b) => a + b, 0)
            } records purged`);
            onDeleted();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally { setBusy(false); }
    };
    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="delete-company-dialog">
                <DialogHeader>
                    <DialogTitle className="text-red-700">{i18n.t("admin.l.Delete_company", "Delete company")}</DialogTitle>
                    <DialogDescription>
                        {i18n.t("admin.deleteCompanyBefore", "This will permanently delete")} <strong>{target.name}</strong>{i18n.t("admin.deleteCompanyAfter", ", all its restaurants, users, invoices, deliveries, inventory and every other record it owns. This action cannot be undone.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                    <label className="text-xs font-semibold text-neutral-700">
                        Type <span className="font-mono">{target.name}</span> to confirm
                    </label>
                    <input data-testid="delete-company-confirm-input"
                           value={confirm}
                           onChange={(e) => setConfirm(e.target.value)}
                           className="w-full h-10 px-3 rounded-md border border-red-200 bg-white text-sm font-mono" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose}
                            className="h-10 px-4 rounded-md border border-neutral-300 bg-white text-sm font-semibold">
                        {i18n.t("admin.l.Cancel", "Cancel")}
                    </button>
                    <button data-testid="delete-company-confirm"
                            onClick={doDelete}
                            disabled={!canConfirm || busy}
                            className="h-10 px-4 rounded-md bg-red-600 text-white text-sm font-semibold disabled:opacity-40">
                        {busy ? i18n.t("admin.l.Deleting", "Deleting…") : i18n.t("admin.l.Delete_permanently", "Delete permanently")}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ============================================================ Restaurants
function RestaurantsTab() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    useEffect(() => {
        api.get("/admin/restaurants", { params: q ? { q } : {} }).then(({ data }) => setRows(data));
    }, [q]);
    return (
        <div data-testid="admin-restaurants">
            <input
                data-testid="admin-restaurants-search"
                value={q} onChange={(e) => setQ(e.target.value)} placeholder={i18n.t("admin.l.Search_restaurants", "Search restaurants")}
                className="mb-4 h-9 px-3 rounded-md border border-neutral-300 text-sm w-full max-w-md"
            />
            <div className="kop-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                        <tr>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Restaurant", "Restaurant")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Company", "Company")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.City", "City")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Active", "Active")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan="4" className="px-4 py-6 text-center text-neutral-500">{i18n.t("admin.l.No_restaurants", "No restaurants.")}</td></tr>
                        )}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-neutral-200">
                                <td className="px-4 py-2 font-medium">{r.name}</td>
                                <td className="px-4 py-2 text-neutral-600 text-xs">{r.company_id}</td>
                                <td className="px-4 py-2">{r.city || "—"}</td>
                                <td className="px-4 py-2">{r.active ? i18n.t("admin.l.Yes", "Yes") : i18n.t("admin.l.No", "No")}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ============================================================ Users
function UsersTab() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [pwdUser, setPwdUser] = useState(null);
    const load = useCallback(() => api.get("/admin/users", { params: q ? { q } : {} }).then(({ data }) => setRows(data)), [q]);
    useEffect(() => { load(); }, [load]);

    const lock = async (u) => {
        try { await api.post(`/admin/users/${u.id}/lock`, { reason: "Admin action" }); toast.success(i18n.t("admin.l.User_suspended", "User suspended")); load(); }
        catch { toast.error(i18n.t("admin.l.Suspend_failed", "Suspend failed")); }
    };
    const unlock = async (u) => {
        try { await api.post(`/admin/users/${u.id}/unlock`); toast.success(i18n.t("admin.l.User_reactivated", "User reactivated")); load(); }
        catch { toast.error(i18n.t("admin.l.Reactivate_failed", "Reactivate failed")); }
    };
    const reset = async (u) => {
        try {
            const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
            toast.success(i18n.t("admin.newTempPassword", { defaultValue: "New temporary password: {{pw}}", pw: data.temporary_password, interpolation: { escapeValue: false } }), { duration: 30000 });
            load();
        } catch { toast.error(i18n.t("admin.l.Reset_failed", "Reset failed")); }
    };

    return (
        <div data-testid="admin-users">
            <input
                data-testid="admin-users-search"
                value={q} onChange={(e) => setQ(e.target.value)} placeholder={i18n.t("admin.l.Search_by_name_or_email", "Search by name or email")}
                className="mb-4 h-9 px-3 rounded-md border border-neutral-300 text-sm w-full max-w-md"
            />
            <div className="kop-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                        <tr>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Name", "Name")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Email", "Email")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Role", "Role")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Status", "Status")}</th>
                            <th className="text-right px-4 py-2">{i18n.t("admin.l.Actions", "Actions")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((u) => (
                            <tr key={u.id} className="border-t border-neutral-200" data-testid={`admin-user-row-${u.id}`}>
                                <td className="px-4 py-2 font-medium">{u.name}</td>
                                <td className="px-4 py-2 text-neutral-600">{u.email}</td>
                                <td className="px-4 py-2 capitalize">{u.role}</td>
                                <td className="px-4 py-2">
                                    {u.locked_at
                                        ? <StatusBadge status="action_required">{i18n.t("admin.l.Suspended", "Suspended")}</StatusBadge>
                                        : <StatusBadge status="completed">{i18n.t("admin.l.Active", "Active")}</StatusBadge>}
                                </td>
                                <td className="px-4 py-2 text-right space-x-1">
                                    {u.locked_at ? (
                                        <button data-testid={`admin-user-unlock-${u.id}`} onClick={() => unlock(u)}
                                                className="h-8 px-2 inline-flex items-center gap-1 rounded border border-neutral-300 text-xs hover:bg-neutral-50">
                                            <Play className="h-3 w-3" /> {i18n.t("admin.l.Reactivate", "Reactivate")}
                                        </button>
                                    ) : (
                                        <button data-testid={`admin-user-lock-${u.id}`} onClick={() => lock(u)}
                                                className="h-8 px-2 inline-flex items-center gap-1 rounded border border-neutral-300 text-xs hover:bg-neutral-50">
                                            <Pause className="h-3 w-3" /> {i18n.t("admin.l.Suspend", "Suspend")}
                                        </button>
                                    )}
                                    <button data-testid={`admin-user-change-${u.id}`} onClick={() => setPwdUser(u)}
                                            className="h-8 px-2 inline-flex items-center gap-1 rounded border border-neutral-300 text-xs hover:bg-neutral-50">
                                        <KeyRound className="h-3 w-3" /> {i18n.t("admin.l.Change_Password", "Change Password")}
                                    </button>
                                    <button data-testid={`admin-user-reset-${u.id}`} onClick={() => reset(u)}
                                            className="h-8 px-2 inline-flex items-center gap-1 rounded border border-neutral-300 text-xs hover:bg-neutral-50">
                                        <KeyRound className="h-3 w-3" /> {i18n.t("admin.l.Reset", "Reset")}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {pwdUser && (
                <ChangePasswordDialog user={pwdUser}
                    onClose={() => setPwdUser(null)}
                    onSaved={() => { setPwdUser(null); load(); }} />
            )}
        </div>
    );
}

function ChangePasswordDialog({ user, onClose, onSaved }) {
    const [pwd, setPwd] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const mismatch = pwd && confirm && pwd !== confirm;
    const tooShort = pwd && pwd.length < 6;
    const canSave = pwd && !mismatch && !tooShort;
    const save = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/users/${user.id}/set-password`,
                { new_password: pwd, confirm_password: confirm });
            toast.success(i18n.t("admin.l.Password_updated", "Password updated"));
            onSaved();
        } catch (e) {
            toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Update_failed", "Update failed")));
        } finally { setBusy(false); }
    };
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="admin-change-password-dialog">
                <DialogHeader>
                    <DialogTitle>{i18n.t("admin.l.Change_password", "Change password")}</DialogTitle>
                    <DialogDescription>
                        {i18n.t("admin.setPasswordFor", "Set a new password for")} <span className="font-semibold">{user.email}</span>{i18n.t("admin.setPasswordAfter", ". The user can log in with the new password immediately.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">
                            {i18n.t("admin.l.New_password", "New password")}
                        </label>
                        <input data-testid="admin-cp-new" type="password"
                            value={pwd} onChange={(e) => setPwd(e.target.value)}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md text-sm" />
                        {tooShort && <div className="text-xs text-rose-600 mt-1">{i18n.t("admin.l.At_least_6_characters", "At least 6 characters.")}</div>}
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">
                            {i18n.t("admin.l.Confirm_new_password", "Confirm new password")}
                        </label>
                        <input data-testid="admin-cp-confirm" type="password"
                            value={confirm} onChange={(e) => setConfirm(e.target.value)}
                            className={`mt-1 w-full h-11 px-3 border rounded-md text-sm ${mismatch ? "border-rose-400" : "border-neutral-300"}`} />
                        {mismatch && <div className="text-xs text-rose-600 mt-1">{i18n.t("admin.l.Passwords_do_not_match", "Passwords do not match.")}</div>}
                    </div>
                </div>
                <DialogFooter>
                    <button onClick={onClose}
                        className="h-10 px-4 rounded-md border border-neutral-300 text-sm hover:bg-neutral-50">
                        {i18n.t("admin.l.Cancel", "Cancel")}
                    </button>
                    <button data-testid="admin-cp-save" onClick={save} disabled={!canSave || busy}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {busy ? i18n.t("admin.l.Saving", "Saving…") : i18n.t("admin.l.Save_password", "Save password")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ============================================================ Subscriptions
function SubscriptionsTab() {
    const [rows, setRows] = useState([]);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/admin/subscriptions").then(({ data }) => setRows(data));
    useEffect(() => { load(); }, []);
    return (
        <div data-testid="admin-subscriptions">
            <div className="mb-4 flex justify-end">
                <button
                    data-testid="admin-subscription-new"
                    onClick={() => setCreating(true)}
                    className="h-9 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold"
                ><Plus className="h-4 w-4" /> {i18n.t("admin.l.New_subscription", "New subscription")}</button>
            </div>
            <div className="kop-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                        <tr>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Company", "Company")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Plan", "Plan")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Status", "Status")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Trial_end", "Trial end")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Renewal", "Renewal")}</th>
                            <th className="px-4 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan="6" className="px-4 py-6 text-center text-neutral-500">{i18n.t("admin.l.No_subscriptions", "No subscriptions.")}</td></tr>
                        )}
                        {rows.map((s) => (
                            <tr key={s.id} className="border-t border-neutral-200" data-testid={`admin-subscription-row-${s.id}`}>
                                <td className="px-4 py-2 font-medium">{s.company?.name || "—"}</td>
                                <td className="px-4 py-2 capitalize">{s.plan}</td>
                                <td className="px-4 py-2 capitalize">{s.status}</td>
                                <td className="px-4 py-2">{s.trial_end || "—"}</td>
                                <td className="px-4 py-2">{s.renewal_date || "—"}</td>
                                <td className="px-4 py-2 text-right">
                                    <button
                                        data-testid={`admin-subscription-edit-${s.id}`}
                                        onClick={() => setEditing(s)}
                                        className="h-8 px-2 inline-flex items-center gap-1 rounded border border-neutral-300 text-xs hover:bg-neutral-50"
                                    ><Edit2 className="h-3 w-3" /> {i18n.t("admin.l.Edit", "Edit")}</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {creating && (
                <NewSubscriptionDialog onClose={() => setCreating(false)}
                    onCreated={() => { setCreating(false); load(); }} />
            )}
            {editing && (
                <SubscriptionEditor seed={editing}
                    onClose={() => setEditing(null)}
                    onDone={() => { setEditing(null); load(); }} />
            )}
        </div>
    );
}

function SubscriptionEditor({ seed, onClose, onDone }) {
    const [form, setForm] = useState({
        plan: seed.plan || "",
        status: seed.status || "active",
        trial_end: seed.trial_end || "",
        renewal_date: seed.renewal_date || "",
        seats: seed.seats ?? "",
        monthly_price: seed.monthly_price ?? "",
    });
    const [busy, setBusy] = useState(false);
    const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const submit = async () => {
        setBusy(true);
        try {
            const payload = { ...form };
            if (payload.seats !== "") payload.seats = Number(payload.seats);
            else delete payload.seats;
            if (payload.monthly_price !== "") payload.monthly_price = Number(payload.monthly_price);
            else delete payload.monthly_price;
            await api.patch(`/admin/subscriptions/by-id/${seed.id}`, payload);
            toast.success(i18n.t("admin.l.Subscription_updated", "Subscription updated"));
            await onDone();
        } catch (e) {
            toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Save_failed", "Save failed")));
        } finally { setBusy(false); }
    };
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="admin-subscription-editor" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Edit subscription — {seed.company?.name || "—"}</DialogTitle>
                    <DialogDescription>{i18n.t("admin.l.Change_the_plan_status_and_renewal_details", "Change the plan, status and renewal details.")}</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    {[
                        ["plan", i18n.t("admin.l.Plan_code", "Plan code")], ["status", i18n.t("admin.l.Status", "Status")],
                        ["trial_end", i18n.t("admin.l.Trial_end_YYYY_MM_DD", "Trial end (YYYY-MM-DD)")],
                        ["renewal_date", i18n.t("admin.l.Renewal_YYYY_MM_DD", "Renewal (YYYY-MM-DD)")],
                        ["seats", i18n.t("admin.l.Seats", "Seats")], ["monthly_price", i18n.t("admin.l.Monthly_price", "Monthly price")],
                    ].map(([k, label]) => (
                        <label key={k} className="block">
                            <span className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{label}</span>
                            <input
                                data-testid={`subscription-edit-${k}`}
                                value={form[k] ?? ""}
                                onChange={(e) => setF(k, e.target.value)}
                                className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm"
                            />
                        </label>
                    ))}
                </div>
                <DialogFooter>
                    <button onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold">{i18n.t("admin.l.Cancel", "Cancel")}</button>
                    <button
                        data-testid="admin-subscription-editor-save"
                        onClick={submit} disabled={busy}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-60"
                    >{busy ? i18n.t("admin.l.Saving", "Saving…") : i18n.t("admin.l.Save", "Save")}</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ============================================================ Reports
function ReportsTab() {
    const [r, setR] = useState(null);
    useEffect(() => { api.get("/admin/reports").then(({ data }) => setR(data)); }, []);
    if (!r) return <div className="text-sm text-neutral-500">{i18n.t("admin.l.Loading", "Loading…")}</div>;
    const monthly = Object.entries(r.monthly_signups || {}).sort();
    return (
        <div data-testid="admin-reports" className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="kop-card p-5">
                <div className="text-xs uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Total_companies", "Total companies")}</div>
                <div className="mt-1 font-display text-3xl font-extrabold">{r.total_companies}</div>
            </div>
            <div className="kop-card p-5">
                <div className="text-xs uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Total_restaurants", "Total restaurants")}</div>
                <div className="mt-1 font-display text-3xl font-extrabold">{r.total_restaurants}</div>
            </div>
            <div className="kop-card p-5">
                <div className="text-xs uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Active_users", "Active users")}</div>
                <div className="mt-1 font-display text-3xl font-extrabold">{r.active_users}</div>
            </div>
            <div className="kop-card p-5 md:col-span-2">
                <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">{i18n.t("admin.l.Restaurants_by_country", "Restaurants by country")}</div>
                {Object.entries(r.restaurants_by_country || {}).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between py-1 text-sm border-b border-neutral-100 last:border-0">
                        <span className="font-medium">{k || "—"}</span><span>{v}</span>
                    </div>
                ))}
            </div>
            <div className="kop-card p-5">
                <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">{i18n.t("admin.l.Subscription_distribution", "Subscription distribution")}</div>
                {Object.entries(r.subscription_distribution || {}).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between py-1 text-sm border-b border-neutral-100 last:border-0">
                        <span className="capitalize">{k}</span><span>{v}</span>
                    </div>
                ))}
            </div>
            <div className="kop-card p-5 md:col-span-3">
                <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">{i18n.t("admin.l.Monthly_signups", "Monthly signups")}</div>
                {monthly.length === 0 ? (
                    <div className="text-sm text-neutral-500 italic">{i18n.t("admin.l.No_data_yet", "No data yet.")}</div>
                ) : monthly.map(([ym, n]) => (
                    <div key={ym} className="flex items-center justify-between py-1 text-sm border-b border-neutral-100 last:border-0">
                        <span>{ym}</span><span>{n}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ============================================================ Notifications
function NotificationsTab() {
    const [list, setList] = useState([]);
    useEffect(() => { api.get("/admin/notifications").then(({ data }) => setList(data)); }, []);
    const tone = (code) => code === "subscription_expired" ? "action_required" : "pending";
    return (
        <div data-testid="admin-notifications" className="space-y-2">
            {list.length === 0 && <div className="text-sm text-neutral-500">{i18n.t("admin.l.No_alerts", "No alerts.")}</div>}
            {list.map((n, i) => (
                <div key={i} className="kop-card p-4 flex items-center justify-between">
                    <div>
                        <div className="font-medium capitalize">{n.code.replaceAll("_", " ")}</div>
                        <div className="text-xs text-neutral-500">
                            {n.company_name || n.company_id} {n.trial_end ? i18n.t("admin.trialEnds", { defaultValue: "· trial ends {{date}}", date: n.trial_end }) : ""}
                        </div>
                    </div>
                    <StatusBadge status={tone(n.code)}>{n.code.split("_")[0]}</StatusBadge>
                </div>
            ))}
        </div>
    );
}

// ============================================================ Lifecycle
function LifecycleTab() {
    const [rows, setRows] = useState([]);
    const [filters, setFilters] = useState({
        q: "", lifecycle_status: "", plan: "",
        country: "", trial_ending_within_days: "",
    });
    const [detail, setDetail] = useState(null); // company drawer

    const load = useCallback(async () => {
        const params = {};
        for (const [k, v] of Object.entries(filters)) {
            if (v !== "" && v !== null) params[k] = v;
        }
        const { data } = await api.get("/admin/companies", { params });
        setRows(data);
    }, [filters]);
    useEffect(() => { load(); }, [load]);

    const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

    return (
        <div data-testid="admin-lifecycle">
            <div className="kop-card p-4 mb-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                <div>
                    <label className="text-[10px] uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Search", "Search")}</label>
                    <input
                        data-testid="lifecycle-filter-q"
                        value={filters.q} onChange={(e) => setF("q", e.target.value)}
                        placeholder={i18n.t("admin.l.Company_name", "Company name")}
                        className="mt-1 h-9 w-full px-3 rounded-md border border-neutral-300 text-sm"
                    />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Lifecycle", "Lifecycle")}</label>
                    <select
                        data-testid="lifecycle-filter-stage"
                        value={filters.lifecycle_status}
                        onChange={(e) => setF("lifecycle_status", e.target.value)}
                        className="mt-1 h-9 w-full px-2 rounded-md border border-neutral-300 text-sm"
                    >
                        <option value="">{i18n.t("admin.l.All_stages", "All stages")}</option>
                        {LIFECYCLE_STAGES.map((s) => (
                            <option key={s} value={s}>{LIFECYCLE_LABEL[s]}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Plan", "Plan")}</label>
                    <input
                        data-testid="lifecycle-filter-plan"
                        value={filters.plan} onChange={(e) => setF("plan", e.target.value)}
                        placeholder={i18n.t("admin.l.e_g_starter", "e.g. starter")}
                        className="mt-1 h-9 w-full px-3 rounded-md border border-neutral-300 text-sm"
                    />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Country", "Country")}</label>
                    <input
                        data-testid="lifecycle-filter-country"
                        value={filters.country} onChange={(e) => setF("country", e.target.value)}
                        placeholder="FI"
                        className="mt-1 h-9 w-full px-3 rounded-md border border-neutral-300 text-sm"
                    />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Trial_ends_days", "Trial ends ≤ days")}</label>
                    <input
                        data-testid="lifecycle-filter-trial-ending"
                        type="number" min="0"
                        value={filters.trial_ending_within_days}
                        onChange={(e) => setF("trial_ending_within_days", e.target.value)}
                        placeholder="e.g. 7"
                        className="mt-1 h-9 w-full px-3 rounded-md border border-neutral-300 text-sm"
                    />
                </div>
            </div>

            <div className="kop-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                        <tr>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Company", "Company")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Lifecycle", "Lifecycle")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Subscription", "Subscription")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Plan", "Plan")}</th>
                            <th className="text-right px-4 py-2">{i18n.t("admin.l.Rest", "Rest.")}</th>
                            <th className="text-right px-4 py-2">{i18n.t("admin.l.Users", "Users")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Country", "Country")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Trial_days", "Trial days")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Assigned", "Assigned")}</th>
                            <th className="px-4 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan="10" className="px-4 py-6 text-center text-neutral-500">{i18n.t("admin.l.No_companies_match", "No companies match.")}</td></tr>
                        )}
                        {rows.map((c) => (
                            <tr key={c.id} className="border-t border-neutral-200" data-testid={`lifecycle-row-${c.id}`}>
                                <td className="px-4 py-2 font-medium">{c.name}</td>
                                <td className="px-4 py-2">
                                    <StatusBadge status={LIFECYCLE_TONE[c.lifecycle_status] || "pending"}>
                                        {LIFECYCLE_LABEL[c.lifecycle_status] || c.lifecycle_status}
                                    </StatusBadge>
                                </td>
                                <td className="px-4 py-2 capitalize">{c.subscription?.status || "—"}</td>
                                <td className="px-4 py-2 capitalize">{c.subscription?.plan || "—"}</td>
                                <td className="px-4 py-2 text-right">{c.restaurant_count}</td>
                                <td className="px-4 py-2 text-right">{c.user_count}</td>
                                <td className="px-4 py-2">{c.country || "—"}</td>
                                <td className="px-4 py-2">
                                    {c.trial_days_remaining == null ? "—"
                                     : c.trial_days_remaining < 0 ? <span className="text-rose-600 font-semibold">{i18n.t("admin.l.expired", "expired")}</span>
                                     : `${c.trial_days_remaining}d`}
                                </td>
                                <td className="px-4 py-2 text-neutral-600 text-xs">{c.assigned_contact || "—"}</td>
                                <td className="px-4 py-2 text-right">
                                    <button
                                        data-testid={`lifecycle-open-${c.id}`}
                                        onClick={() => setDetail(c)}
                                        className="h-8 px-3 rounded border border-neutral-300 text-xs font-semibold hover:bg-neutral-50"
                                    >{i18n.t("admin.l.Open", "Open")}</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {detail && (
                <CompanyLifecycleDrawer
                    company={detail}
                    onClose={() => setDetail(null)}
                    onSaved={async () => { await load(); }}
                />
            )}
        </div>
    );
}

function CompanyLifecycleDrawer({ company, onClose, onSaved }) {
    const [tab, setTab] = useState("overview");
    const [stage, setStage] = useState(company.lifecycle_status || "lead");
    const [assignedContact, setAssignedContact] = useState(company.assigned_contact || "");
    const [notes, setNotes] = useState([]);
    const [events, setEvents] = useState([]);
    const [newNote, setNewNote] = useState("");
    const [busy, setBusy] = useState(false);

    const loadAux = useCallback(async () => {
        const [n, e] = await Promise.all([
            api.get(`/admin/companies/${company.id}/notes`),
            api.get(`/admin/companies/${company.id}/lifecycle-events`),
        ]);
        setNotes(n.data || []);
        setEvents(e.data || []);
    }, [company.id]);
    useEffect(() => { loadAux(); }, [loadAux]);

    const saveStage = async () => {
        setBusy(true);
        try {
            await api.patch(`/admin/companies/${company.id}`, {
                lifecycle_status: stage, assigned_contact: assignedContact,
            });
            toast.success(i18n.t("admin.l.Lifecycle_updated", "Lifecycle updated"));
            await loadAux();
            await onSaved();
        } catch (e) {
            toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Update_failed", "Update failed")));
        } finally { setBusy(false); }
    };
    const addNote = async () => {
        if (!newNote.trim()) return;
        setBusy(true);
        try {
            await api.post(`/admin/companies/${company.id}/notes`, { text: newNote.trim() });
            setNewNote("");
            await loadAux();
            toast.success(i18n.t("admin.l.Note_added", "Note added"));
        } catch { toast.error(i18n.t("admin.l.Failed_to_add_note", "Failed to add note")); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50" data-testid="lifecycle-drawer">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <aside className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-xl flex flex-col">
                <header className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
                    <div>
                        <div className="font-semibold text-lg">{company.name}</div>
                        <div className="text-xs text-neutral-500">{company.country || "—"}</div>
                    </div>
                    <button data-testid="lifecycle-drawer-close" onClick={onClose}
                            className="h-8 w-8 rounded hover:bg-neutral-100 grid place-items-center">
                        <X className="h-4 w-4" />
                    </button>
                </header>
                <nav className="flex border-b border-neutral-200 text-sm">
                    {["overview", "notes", "events"].map((t) => (
                        <button key={t}
                                data-testid={`drawer-tab-${t}`}
                                onClick={() => setTab(t)}
                                className={`px-4 py-2 font-semibold capitalize border-b-2 ${
                                    tab === t ? "border-neutral-900" : "border-transparent text-neutral-500"
                                }`}>
                            {t}
                        </button>
                    ))}
                </nav>
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {tab === "overview" && (
                        <>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                                    {i18n.t("admin.l.Lifecycle_stage", "Lifecycle stage")}
                                </label>
                                <select
                                    data-testid="drawer-stage-select"
                                    value={stage}
                                    onChange={(e) => setStage(e.target.value)}
                                    className="mt-1 h-10 w-full px-3 rounded-md border border-neutral-300 text-sm"
                                >
                                    {LIFECYCLE_STAGES.map((s) => (
                                        <option key={s} value={s}>{LIFECYCLE_LABEL[s]}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                                    {i18n.t("admin.l.Assigned_MiseOne_contact", "Assigned MiseOne contact")}
                                </label>
                                <input
                                    data-testid="drawer-assigned-contact"
                                    value={assignedContact}
                                    onChange={(e) => setAssignedContact(e.target.value)}
                                    placeholder="email@miseone.com"
                                    className="mt-1 h-10 w-full px-3 rounded-md border border-neutral-300 text-sm"
                                />
                            </div>
                            <button
                                data-testid="drawer-save"
                                onClick={saveStage}
                                disabled={busy}
                                className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50"
                            >{i18n.t("admin.l.Save_changes", "Save changes")}</button>

                            <div className="pt-4 border-t border-neutral-100 grid grid-cols-2 gap-3 text-sm">
                                <Stat label={i18n.t("admin.l.Restaurants", "Restaurants")} value={company.restaurant_count} />
                                <Stat label={i18n.t("admin.l.Users", "Users")} value={company.user_count} />
                                <Stat label={i18n.t("admin.l.Plan", "Plan")} value={company.subscription?.plan || "—"} />
                                <Stat label={i18n.t("admin.l.Sub_status", "Sub status")} value={company.subscription?.status || "—"} />
                                <Stat label={i18n.t("admin.l.Trial_end", "Trial end")} value={company.subscription?.trial_end || "—"} />
                                <Stat label={i18n.t("admin.l.Created", "Created")} value={(company.created_at || "").slice(0, 10) || "—"} />
                            </div>
                        </>
                    )}
                    {tab === "notes" && (
                        <>
                            <div className="flex gap-2">
                                <input
                                    data-testid="drawer-note-input"
                                    value={newNote}
                                    onChange={(e) => setNewNote(e.target.value)}
                                    placeholder={i18n.t("admin.l.Add_an_internal_note", "Add an internal note…")}
                                    className="h-10 flex-1 px-3 rounded-md border border-neutral-300 text-sm"
                                />
                                <button
                                    data-testid="drawer-note-add"
                                    onClick={addNote}
                                    disabled={busy || !newNote.trim()}
                                    className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1"
                                ><Plus className="h-4 w-4" /> {i18n.t("admin.l.Add", "Add")}</button>
                            </div>
                            {notes.length === 0 ? (
                                <div className="text-sm text-neutral-500 italic">{i18n.t("admin.l.No_notes_yet", "No notes yet.")}</div>
                            ) : (
                                <ul className="space-y-3" data-testid="drawer-notes-list">
                                    {notes.map((n) => (
                                        <li key={n.id} className="kop-card p-3">
                                            <div className="text-sm">{n.text}</div>
                                            <div className="text-[11px] text-neutral-500 mt-1">
                                                {n.author_email} · {(n.created_at || "").slice(0, 16).replace("T", " ")}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                    {tab === "events" && (
                        <>
                            {events.length === 0 ? (
                                <div className="text-sm text-neutral-500 italic">{i18n.t("admin.l.No_lifecycle_changes_yet", "No lifecycle changes yet.")}</div>
                            ) : (
                                <ul className="space-y-2" data-testid="drawer-events-list">
                                    {events.map((e) => (
                                        <li key={e.id} className="kop-card p-3 flex items-center justify-between">
                                            <div className="text-sm">
                                                <span className="capitalize">{LIFECYCLE_LABEL[e.from_status] || e.from_status || "—"}</span>
                                                <span className="mx-2">→</span>
                                                <span className="capitalize font-semibold">{LIFECYCLE_LABEL[e.to_status] || e.to_status}</span>
                                            </div>
                                            <div className="text-[11px] text-neutral-500 text-right">
                                                <div>{e.admin_email}</div>
                                                <div>{(e.created_at || "").slice(0, 16).replace("T", " ")}</div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>
            </aside>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="rounded-md border border-neutral-200 p-2">
            <div className="text-[10px] uppercase tracking-wide text-neutral-500">{label}</div>
            <div className="text-sm font-semibold">{value}</div>
        </div>
    );
}

// ============================================================ Plans
function PlansTab() {
    const [rows, setRows] = useState([]);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/admin/plans").then(({ data }) => setRows(data));
    useEffect(() => { load(); }, []);
    return (
        <div data-testid="admin-plans">
            <div className="mb-4 flex justify-end">
                <button
                    data-testid="admin-plans-new"
                    onClick={() => setCreating(true)}
                    className="h-9 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold"
                ><Plus className="h-4 w-4" /> {i18n.t("admin.l.New_plan", "New plan")}</button>
            </div>
            <div className="kop-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                        <tr>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Code", "Code")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Name", "Name")}</th>
                            <th className="text-right px-4 py-2">{i18n.t("admin.l.Monthly", "Monthly")}</th>
                            <th className="text-right px-4 py-2">{i18n.t("admin.l.Yearly", "Yearly")}</th>
                            <th className="text-right px-4 py-2">{i18n.t("admin.l.Rest_limit", "Rest. limit")}</th>
                            <th className="text-right px-4 py-2">{i18n.t("admin.l.User_limit", "User limit")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Support", "Support")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Active", "Active")}</th>
                            <th className="px-4 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan="9" className="px-4 py-6 text-center text-neutral-500">{i18n.t("admin.l.No_plans_yet", "No plans yet.")}</td></tr>
                        )}
                        {rows.map((p) => (
                            <tr key={p.id} className="border-t border-neutral-200" data-testid={`plan-row-${p.code}`}>
                                <td className="px-4 py-2 font-mono text-xs">{p.code}</td>
                                <td className="px-4 py-2 font-medium">{p.name}</td>
                                <td className="px-4 py-2 text-right">{p.monthly_price} {p.currency}</td>
                                <td className="px-4 py-2 text-right">{p.yearly_price} {p.currency}</td>
                                <td className="px-4 py-2 text-right">{p.restaurant_limit ?? "∞"}</td>
                                <td className="px-4 py-2 text-right">{p.user_limit ?? "∞"}</td>
                                <td className="px-4 py-2 capitalize">{p.support_level}</td>
                                <td className="px-4 py-2">
                                    <StatusBadge status={p.active ? "completed" : "low"}>
                                        {p.active ? i18n.t("admin.l.Active", "Active") : i18n.t("admin.l.Archived", "Archived")}
                                    </StatusBadge>
                                </td>
                                <td className="px-4 py-2 text-right">
                                    <button
                                        data-testid={`plan-edit-${p.code}`}
                                        onClick={() => setEditing(p)}
                                        className="h-8 px-2 inline-flex items-center gap-1 rounded border border-neutral-300 text-xs hover:bg-neutral-50"
                                    ><Edit2 className="h-3 w-3" /> {i18n.t("admin.l.Edit", "Edit")}</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {(creating || editing) && (
                <PlanEditor
                    seed={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onDone={async () => { setCreating(false); setEditing(null); await load(); }}
                />
            )}
        </div>
    );
}

function PlanEditor({ seed, onClose, onDone }) {
    const isEdit = !!seed;
    const [form, setForm] = useState({
        code: seed?.code || "",
        name: seed?.name || "",
        description: seed?.description || "",
        monthly_price: seed?.monthly_price ?? 0,
        yearly_price: seed?.yearly_price ?? 0,
        currency: seed?.currency || "EUR",
        restaurant_limit: seed?.restaurant_limit ?? "",
        user_limit: seed?.user_limit ?? "",
        storage_limit_gb: seed?.storage_limit_gb ?? "",
        ocr_limit: seed?.ocr_limit ?? "",
        ai_limit: seed?.ai_limit ?? "",
        support_level: seed?.support_level || "standard",
        active: seed?.active ?? true,
    });
    const [busy, setBusy] = useState(false);
    const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const num = (v) => v === "" || v === null ? null : Number(v);
    const submit = async () => {
        setBusy(true);
        try {
            const payload = {
                ...form,
                monthly_price: Number(form.monthly_price) || 0,
                yearly_price: Number(form.yearly_price) || 0,
                restaurant_limit: num(form.restaurant_limit),
                user_limit: num(form.user_limit),
                storage_limit_gb: num(form.storage_limit_gb),
                ocr_limit: num(form.ocr_limit),
                ai_limit: num(form.ai_limit),
            };
            if (isEdit) {
                const { code, ...patch } = payload;  // code immutable
                void code;
                await api.patch(`/admin/plans/${seed.id}`, patch);
                toast.success(i18n.t("admin.l.Plan_updated", "Plan updated"));
            } else {
                await api.post("/admin/plans", payload);
                toast.success(i18n.t("admin.l.Plan_created", "Plan created"));
            }
            await onDone();
        } catch (e) {
            toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Save_failed", "Save failed")));
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50" data-testid="plan-editor">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
                <header className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
                    <div className="font-semibold">{isEdit ? i18n.t("admin.l.Edit_plan", "Edit plan") : i18n.t("admin.l.New_plan", "New plan")}</div>
                    <button onClick={onClose} className="h-8 w-8 rounded hover:bg-neutral-100 grid place-items-center">
                        <X className="h-4 w-4" />
                    </button>
                </header>
                <div className="flex-1 overflow-y-auto p-6 space-y-3 text-sm">
                    <Field label={i18n.t("admin.l.Code_slug", "Code (slug)")}>
                        <input
                            data-testid="plan-form-code"
                            value={form.code}
                            disabled={isEdit}
                            onChange={(e) => setF("code", e.target.value.toLowerCase().replace(/\s+/g, "_"))}
                            className="h-10 w-full px-3 rounded-md border border-neutral-300 disabled:bg-neutral-100"
                        />
                    </Field>
                    <Field label={i18n.t("admin.l.Display_name", "Display name")}>
                        <input
                            data-testid="plan-form-name"
                            value={form.name}
                            onChange={(e) => setF("name", e.target.value)}
                            className="h-10 w-full px-3 rounded-md border border-neutral-300"
                        />
                    </Field>
                    <Field label={i18n.t("admin.l.Description", "Description")}>
                        <textarea
                            data-testid="plan-form-description"
                            value={form.description}
                            onChange={(e) => setF("description", e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 rounded-md border border-neutral-300"
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label={i18n.t("admin.l.Monthly_price", "Monthly price")}>
                            <input data-testid="plan-form-monthly" type="number" min="0" step="0.01"
                                value={form.monthly_price} onChange={(e) => setF("monthly_price", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                        </Field>
                        <Field label={i18n.t("admin.l.Yearly_price", "Yearly price")}>
                            <input data-testid="plan-form-yearly" type="number" min="0" step="0.01"
                                value={form.yearly_price} onChange={(e) => setF("yearly_price", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                        </Field>
                        <Field label={i18n.t("admin.l.Currency", "Currency")}>
                            <input value={form.currency} onChange={(e) => setF("currency", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                        </Field>
                        <Field label={i18n.t("admin.l.Support_level", "Support level")}>
                            <select
                                data-testid="plan-form-support"
                                value={form.support_level}
                                onChange={(e) => setF("support_level", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300"
                            >
                                {["community", "standard", "priority", "dedicated"].map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label={i18n.t("admin.l.Restaurant_limit_blank", "Restaurant limit (blank = ∞)")}>
                            <input value={form.restaurant_limit ?? ""} onChange={(e) => setF("restaurant_limit", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                        </Field>
                        <Field label={i18n.t("admin.l.User_limit", "User limit")}>
                            <input value={form.user_limit ?? ""} onChange={(e) => setF("user_limit", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                        </Field>
                        <Field label={i18n.t("admin.l.Storage_limit_GB", "Storage limit (GB)")}>
                            <input value={form.storage_limit_gb ?? ""} onChange={(e) => setF("storage_limit_gb", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                        </Field>
                        <Field label={i18n.t("admin.l.OCR_limit_future", "OCR limit (future)")}>
                            <input value={form.ocr_limit ?? ""} onChange={(e) => setF("ocr_limit", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                        </Field>
                        <Field label={i18n.t("admin.l.AI_limit_future", "AI limit (future)")}>
                            <input value={form.ai_limit ?? ""} onChange={(e) => setF("ai_limit", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                        </Field>
                        <Field label={i18n.t("admin.l.Active", "Active")}>
                            <select
                                data-testid="plan-form-active"
                                value={form.active ? "1" : "0"}
                                onChange={(e) => setF("active", e.target.value === "1")}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300"
                            >
                                <option value="1">{i18n.t("admin.l.Yes", "Yes")}</option>
                                <option value="0">{i18n.t("admin.l.Archived", "Archived")}</option>
                            </select>
                        </Field>
                    </div>
                </div>
                <footer className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-2">
                    <button onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold">
                        {i18n.t("admin.l.Cancel", "Cancel")}
                    </button>
                    <button data-testid="plan-form-save" onClick={submit} disabled={busy}
                            className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
                        <Check className="h-4 w-4" /> {isEdit ? i18n.t("admin.l.Save", "Save") : i18n.t("admin.l.Create", "Create")}
                    </button>
                </footer>
            </aside>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-wide text-neutral-500">{label}</span>
            <div className="mt-1">{children}</div>
        </label>
    );
}

// ============================================================ Support
function SupportTab() {
    const [rows, setRows] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [filters, setFilters] = useState({ status: "", priority: "", follow_up_due: false });
    const [creating, setCreating] = useState(false);
    const load = useCallback(async () => {
        const params = {};
        if (filters.status) params.status = filters.status;
        if (filters.priority) params.priority = filters.priority;
        if (filters.follow_up_due) params.follow_up_due = true;
        const [n, c] = await Promise.all([
            api.get("/admin/support-notes", { params }),
            api.get("/admin/companies"),
        ]);
        setRows(n.data || []);
        setCompanies(c.data || []);
    }, [filters]);
    useEffect(() => { load(); }, [load]);

    const updateNote = async (n, patch) => {
        try {
            await api.patch(`/admin/support-notes/${n.id}`, patch);
            toast.success(i18n.t("admin.l.Updated", "Updated"));
            await load();
        } catch (e) {
            toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Update_failed", "Update failed")));
        }
    };

    return (
        <div data-testid="admin-support">
            <div className="kop-card p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
                <div>
                    <label className="text-[10px] uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Status", "Status")}</label>
                    <select
                        data-testid="support-filter-status"
                        value={filters.status}
                        onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
                        className="mt-1 h-9 w-full px-2 rounded-md border border-neutral-300 text-sm"
                    >
                        <option value="">{i18n.t("admin.l.All", "All")}</option>
                        {SUPPORT_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Priority", "Priority")}</label>
                    <select
                        data-testid="support-filter-priority"
                        value={filters.priority}
                        onChange={(e) => setFilters((p) => ({ ...p, priority: e.target.value }))}
                        className="mt-1 h-9 w-full px-2 rounded-md border border-neutral-300 text-sm"
                    >
                        <option value="">{i18n.t("admin.l.All", "All")}</option>
                        {SUPPORT_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
                <label className="flex items-center gap-2 text-sm">
                    <input
                        data-testid="support-filter-followup-due"
                        type="checkbox"
                        checked={filters.follow_up_due}
                        onChange={(e) => setFilters((p) => ({ ...p, follow_up_due: e.target.checked }))}
                    />
                    {i18n.t("admin.l.Follow_up_due", "Follow-up due")}
                </label>
                <div className="text-right">
                    <button
                        data-testid="support-new"
                        onClick={() => setCreating(true)}
                        className="h-9 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold inline-flex items-center gap-2"
                    ><Plus className="h-4 w-4" /> {i18n.t("admin.l.New_note", "New note")}</button>
                </div>
            </div>

            <div className="space-y-3">
                {rows.length === 0 && (
                    <div className="kop-card p-6 text-center text-sm text-neutral-500">{i18n.t("admin.l.No_support_notes_match", "No support notes match.")}</div>
                )}
                {rows.map((n) => (
                    <div key={n.id} className="kop-card p-4" data-testid={`support-row-${n.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <StatusBadge status={n.priority === "high" ? "action_required"
                                                       : n.priority === "medium" ? "pending" : "low"}>
                                        {n.priority}
                                    </StatusBadge>
                                    <StatusBadge status={n.status === "closed" || n.status === "resolved" ? "completed" : "pending"}>
                                        {n.status.replace("_", " ")}
                                    </StatusBadge>
                                    <span className="font-semibold">{n.company_name}</span>
                                </div>
                                {n.source === "restaurant" ? (
                                    <div className="text-xs text-neutral-600 mt-1" data-testid={`support-origin-${n.id}`}>
                                        {i18n.t("admin.l.Restaurant_request_from", {
                                            defaultValue: "Restaurant request · {{restaurant}} · {{author}}",
                                            restaurant: n.restaurant_name || n.restaurant_id, author: n.author_email })}
                                    </div>
                                ) : null}
                                {n.subject ? <div className="text-sm font-medium mt-2" data-testid={`support-subject-${n.id}`}>{n.subject}</div> : null}
                                <div className="text-sm mt-2">{n.text}</div>
                                <div className="text-[11px] text-neutral-500 mt-1">
                                    {n.contact_person && <>Contact: {n.contact_person} · </>}
                                    {n.follow_up_date && <>Follow-up: {n.follow_up_date} · </>}
                                    {(n.created_at || "").slice(0, 10)}
                                </div>
                            </div>
                            <div className="flex flex-col gap-1">
                                <select
                                    data-testid={`support-status-${n.id}`}
                                    value={n.status}
                                    onChange={(e) => updateNote(n, { status: e.target.value })}
                                    className="h-8 text-xs px-2 rounded border border-neutral-300"
                                >
                                    {SUPPORT_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                                </select>
                                <select
                                    data-testid={`support-priority-${n.id}`}
                                    value={n.priority}
                                    onChange={(e) => updateNote(n, { priority: e.target.value })}
                                    className="h-8 text-xs px-2 rounded border border-neutral-300"
                                >
                                    {SUPPORT_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {creating && (
                <SupportNoteEditor
                    companies={companies}
                    onClose={() => setCreating(false)}
                    onDone={async () => { setCreating(false); await load(); }}
                />
            )}
        </div>
    );
}

function SupportNoteEditor({ companies, onClose, onDone }) {
    const [form, setForm] = useState({
        company_id: companies[0]?.id || "",
        text: "",
        priority: "medium",
        status: "open",
        contact_person: "",
        follow_up_date: "",
    });
    const [busy, setBusy] = useState(false);
    const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const canSave = form.company_id && form.text.trim().length > 0;
    const submit = async () => {
        setBusy(true);
        try {
            await api.post("/admin/support-notes", {
                ...form,
                follow_up_date: form.follow_up_date || null,
            });
            toast.success(i18n.t("admin.l.Support_note_created", "Support note created"));
            await onDone();
        } catch (e) {
            toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Save_failed", "Save failed")));
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50" data-testid="support-editor">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
                <header className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
                    <div className="font-semibold inline-flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" /> {i18n.t("admin.l.New_support_note", "New support note")}
                    </div>
                    <button onClick={onClose} className="h-8 w-8 rounded hover:bg-neutral-100 grid place-items-center">
                        <X className="h-4 w-4" />
                    </button>
                </header>
                <div className="flex-1 overflow-y-auto p-6 space-y-3 text-sm">
                    <Field label={i18n.t("admin.l.Company", "Company")}>
                        <select
                            data-testid="support-form-company"
                            value={form.company_id}
                            onChange={(e) => setF("company_id", e.target.value)}
                            className="h-10 w-full px-3 rounded-md border border-neutral-300"
                        >
                            {companies.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label={i18n.t("admin.l.Note", "Note")}>
                        <textarea
                            data-testid="support-form-text"
                            value={form.text}
                            onChange={(e) => setF("text", e.target.value)}
                            rows={4}
                            placeholder={i18n.t("admin.l.What_does_the_customer_need", "What does the customer need?")}
                            className="w-full px-3 py-2 rounded-md border border-neutral-300"
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label={i18n.t("admin.l.Priority", "Priority")}>
                            <select
                                data-testid="support-form-priority"
                                value={form.priority}
                                onChange={(e) => setF("priority", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300"
                            >
                                {SUPPORT_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </Field>
                        <Field label={i18n.t("admin.l.Status", "Status")}>
                            <select
                                value={form.status}
                                onChange={(e) => setF("status", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300"
                            >
                                {SUPPORT_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                            </select>
                        </Field>
                        <Field label={i18n.t("admin.l.Contact_person", "Contact person")}>
                            <input
                                value={form.contact_person}
                                onChange={(e) => setF("contact_person", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300"
                            />
                        </Field>
                        <Field label={i18n.t("admin.l.Follow_up_date", "Follow-up date")}>
                            <input
                                data-testid="support-form-followup"
                                type="date"
                                value={form.follow_up_date}
                                onChange={(e) => setF("follow_up_date", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300"
                            />
                        </Field>
                    </div>
                </div>
                <footer className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-2">
                    <button onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold">
                        {i18n.t("admin.l.Cancel", "Cancel")}
                    </button>
                    <button data-testid="support-form-save" onClick={submit} disabled={busy || !canSave}
                            className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50">
                        {i18n.t("admin.l.Save", "Save")}
                    </button>
                </footer>
            </aside>
        </div>
    );
}

// ============================================================ Feature Requests
const FEATURE_STATUSES = ["proposed", "in_review", "planned", "in_progress", "shipped", "declined"];
const SEVERITIES = ["critical", "high", "medium", "low"];
const BUG_STATUSES = ["new", "triaged", "in_progress", "fixed", "verified", "closed"];
const TRAINING_COURSES_F = ["login", "inventory", "deliveries", "recipes",
                              "weekly_menu", "production_review", "reports", "workflow"];
const TRAINING_ROLES_F = ["restaurant_admin", "head_chef", "kitchen_staff", "corporate"];
const TRAINING_STATUSES_F = ["not_started", "in_progress", "completed"];

function FeatureRequestsTab() {
    const [rows, setRows] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [filters, setFilters] = useState({ status: "", priority: "" });
    const [creating, setCreating] = useState(false);
    const [linking, setLinking] = useState(null);

    const load = useCallback(async () => {
        const params = {};
        if (filters.status) params.status = filters.status;
        if (filters.priority) params.priority = filters.priority;
        const [r, c] = await Promise.all([
            api.get("/admin/feature-requests", { params }),
            api.get("/admin/companies"),
        ]);
        setRows(r.data || []);
        setCompanies(c.data || []);
    }, [filters]);
    useEffect(() => { load(); }, [load]);

    const setStatus = async (fr, status) => {
        try { await api.patch(`/admin/feature-requests/${fr.id}`, { status }); await load(); }
        catch (e) { toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Update_failed", "Update failed"))); }
    };

    return (
        <div data-testid="admin-features">
            <div className="kop-card p-4 mb-4 flex flex-wrap gap-3 items-end">
                <div>
                    <label className="text-[10px] uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Status", "Status")}</label>
                    <select data-testid="features-filter-status"
                            value={filters.status}
                            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
                            className="mt-1 h-9 px-2 rounded-md border border-neutral-300 text-sm">
                        <option value="">{i18n.t("admin.l.All", "All")}</option>
                        {FEATURE_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Priority", "Priority")}</label>
                    <select data-testid="features-filter-priority"
                            value={filters.priority}
                            onChange={(e) => setFilters((p) => ({ ...p, priority: e.target.value }))}
                            className="mt-1 h-9 px-2 rounded-md border border-neutral-300 text-sm">
                        <option value="">{i18n.t("admin.l.All", "All")}</option>
                        {SUPPORT_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
                <div className="ml-auto">
                    <button data-testid="features-new"
                            onClick={() => setCreating(true)}
                            className="h-9 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold inline-flex items-center gap-2">
                        <Plus className="h-4 w-4" /> {i18n.t("admin.l.New_feature_request", "New feature request")}
                    </button>
                </div>
            </div>

            <div className="space-y-3">
                {rows.length === 0 && (
                    <div className="kop-card p-6 text-center text-sm text-neutral-500">{i18n.t("admin.l.No_feature_requests_yet", "No feature requests yet.")}</div>
                )}
                {rows.map((f) => (
                    <div key={f.id} className="kop-card p-4" data-testid={`feature-row-${f.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <StatusBadge status={f.priority === "high" ? "action_required"
                                                       : f.priority === "medium" ? "pending" : "low"}>
                                        {f.priority}
                                    </StatusBadge>
                                    <StatusBadge status={
                                        f.status === "shipped" ? "completed"
                                          : f.status === "declined" ? "action_required" : "pending"}>
                                        {f.status.replace("_", " ")}
                                    </StatusBadge>
                                    {f.module && <span className="text-xs text-neutral-500 uppercase tracking-wide">{f.module}</span>}
                                </div>
                                <div className="font-semibold mt-2">{f.title}</div>
                                {f.description && (
                                    <div className="text-sm text-neutral-600 mt-1">{f.description}</div>
                                )}
                                <div className="text-[11px] text-neutral-500 mt-2 flex items-center gap-3 flex-wrap">
                                    <span data-testid={`feature-count-${f.id}`}>
                                        Requested by: <strong>{f.requested_by_count}</strong> customer{f.requested_by_count === 1 ? "" : "s"}
                                    </span>
                                    {(f.requesting_companies || []).slice(0, 3).map((c) => (
                                        <span key={c.company_id} className="text-neutral-600">· {c.company_name}</span>
                                    ))}
                                    {f.target_release && <span>· Target: {f.target_release}</span>}
                                </div>
                            </div>
                            <div className="flex flex-col gap-1 min-w-[10rem]">
                                <select data-testid={`feature-status-${f.id}`}
                                        value={f.status}
                                        onChange={(e) => setStatus(f, e.target.value)}
                                        className="h-8 text-xs px-2 rounded border border-neutral-300">
                                    {FEATURE_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                                </select>
                                <button data-testid={`feature-link-${f.id}`}
                                        onClick={() => setLinking(f)}
                                        className="h-8 px-2 rounded border border-neutral-300 text-xs hover:bg-neutral-50 inline-flex items-center justify-center gap-1">
                                    <Plus className="h-3 w-3" /> {i18n.t("admin.l.Link_customer", "Link customer")}
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {creating && (
                <FeatureRequestEditor
                    companies={companies}
                    onClose={() => setCreating(false)}
                    onDone={async () => { setCreating(false); await load(); }} />
            )}
            {linking && (
                <LinkCustomerDialog
                    title={i18n.t("admin.l.Link_a_customer_to_this_feature_request", "Link a customer to this feature request")}
                    feature={linking}
                    companies={companies}
                    endpoint={`/admin/feature-requests/${linking.id}/link-company`}
                    onClose={() => setLinking(null)}
                    onDone={async () => { setLinking(null); await load(); }} />
            )}
        </div>
    );
}

function FeatureRequestEditor({ companies, onClose, onDone }) {
    const [form, setForm] = useState({
        title: "", description: "", module: "",
        priority: "medium", status: "proposed",
        source_company_id: companies[0]?.id || "",
        source_requested_by: "", target_release: "",
    });
    const [busy, setBusy] = useState(false);
    const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const canSave = form.title.trim().length > 0;
    const submit = async () => {
        setBusy(true);
        try {
            await api.post("/admin/feature-requests", form);
            toast.success(i18n.t("admin.l.Feature_request_created", "Feature request created"));
            await onDone();
        } catch (e) {
            toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Save_failed", "Save failed")));
        } finally { setBusy(false); }
    };
    return (
        <Drawer title={i18n.t("admin.l.New_feature_request", "New feature request")} testid="feature-editor" onClose={onClose}>
            <div className="space-y-3 text-sm">
                <Field label={i18n.t("admin.l.Title", "Title")}>
                    <input data-testid="feature-form-title" value={form.title}
                        onChange={(e) => setF("title", e.target.value)}
                        className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                </Field>
                <Field label={i18n.t("admin.l.Description", "Description")}>
                    <textarea value={form.description}
                        onChange={(e) => setF("description", e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 rounded-md border border-neutral-300" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label={i18n.t("admin.l.Module", "Module")}>
                        <input value={form.module}
                            onChange={(e) => setF("module", e.target.value)}
                            placeholder={i18n.t("admin.l.inventory_recipes", "inventory, recipes, …")}
                            className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                    </Field>
                    <Field label={i18n.t("admin.l.Priority", "Priority")}>
                        <select data-testid="feature-form-priority"
                                value={form.priority}
                                onChange={(e) => setF("priority", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300">
                            {SUPPORT_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </Field>
                    <Field label={i18n.t("admin.l.Source_customer", "Source customer")}>
                        <select data-testid="feature-form-company"
                                value={form.source_company_id}
                                onChange={(e) => setF("source_company_id", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300">
                            <option value="">{i18n.t("admin.l.none", "— none —")}</option>
                            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </Field>
                    <Field label={i18n.t("admin.l.Requested_by", "Requested by")}>
                        <input value={form.source_requested_by}
                            onChange={(e) => setF("source_requested_by", e.target.value)}
                            className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                    </Field>
                    <Field label={i18n.t("admin.l.Target_release", "Target release")}>
                        <input value={form.target_release}
                            onChange={(e) => setF("target_release", e.target.value)}
                            placeholder="2026-Q3"
                            className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                    </Field>
                </div>
            </div>
            <DrawerFooter
                onCancel={onClose}
                onSave={submit}
                saveTestid="feature-form-save"
                disabled={busy || !canSave} />
        </Drawer>
    );
}

// ============================================================ Bug Reports
function BugReportsTab() {
    const [rows, setRows] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [filters, setFilters] = useState({ status: "", severity: "" });
    const [creating, setCreating] = useState(false);
    const [linking, setLinking] = useState(null);

    const load = useCallback(async () => {
        const params = {};
        if (filters.status) params.status = filters.status;
        if (filters.severity) params.severity = filters.severity;
        const [r, c] = await Promise.all([
            api.get("/admin/bug-reports", { params }),
            api.get("/admin/companies"),
        ]);
        setRows(r.data || []);
        setCompanies(c.data || []);
    }, [filters]);
    useEffect(() => { load(); }, [load]);

    const setStatus = async (b, status) => {
        try { await api.patch(`/admin/bug-reports/${b.id}`, { status }); await load(); }
        catch (e) { toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Update_failed", "Update failed"))); }
    };

    return (
        <div data-testid="admin-bugs">
            <div className="kop-card p-4 mb-4 flex flex-wrap gap-3 items-end">
                <div>
                    <label className="text-[10px] uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Status", "Status")}</label>
                    <select data-testid="bugs-filter-status"
                            value={filters.status}
                            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
                            className="mt-1 h-9 px-2 rounded-md border border-neutral-300 text-sm">
                        <option value="">{i18n.t("admin.l.All", "All")}</option>
                        {BUG_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Severity", "Severity")}</label>
                    <select data-testid="bugs-filter-severity"
                            value={filters.severity}
                            onChange={(e) => setFilters((p) => ({ ...p, severity: e.target.value }))}
                            className="mt-1 h-9 px-2 rounded-md border border-neutral-300 text-sm">
                        <option value="">{i18n.t("admin.l.All", "All")}</option>
                        {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div className="ml-auto">
                    <button data-testid="bugs-new"
                            onClick={() => setCreating(true)}
                            className="h-9 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold inline-flex items-center gap-2">
                        <Plus className="h-4 w-4" /> {i18n.t("admin.l.New_bug_report", "New bug report")}
                    </button>
                </div>
            </div>

            <div className="space-y-3">
                {rows.length === 0 && (
                    <div className="kop-card p-6 text-center text-sm text-neutral-500">{i18n.t("admin.l.No_bug_reports", "No bug reports.")}</div>
                )}
                {rows.map((b) => (
                    <div key={b.id} className="kop-card p-4" data-testid={`bug-row-${b.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <StatusBadge status={b.severity === "critical" || b.severity === "high"
                                                       ? "action_required"
                                                       : b.severity === "medium" ? "pending" : "low"}>
                                        {b.severity}
                                    </StatusBadge>
                                    <StatusBadge status={
                                        b.status === "closed" || b.status === "verified" ? "completed"
                                          : "pending"}>
                                        {b.status.replace("_", " ")}
                                    </StatusBadge>
                                    {b.module && <span className="text-xs text-neutral-500 uppercase tracking-wide">{b.module}</span>}
                                </div>
                                <div className="font-semibold mt-2">{b.title}</div>
                                {b.description && (
                                    <div className="text-sm text-neutral-600 mt-1">{b.description}</div>
                                )}
                                <div className="text-[11px] text-neutral-500 mt-2 flex items-center gap-3 flex-wrap">
                                    <span data-testid={`bug-affected-${b.id}`}>
                                        Affected companies: <strong>{b.affected_companies_count}</strong>
                                    </span>
                                    {b.assigned_developer && <span>· Owner: {b.assigned_developer}</span>}
                                    {b.target_release && <span>· Target: {b.target_release}</span>}
                                </div>
                            </div>
                            <div className="flex flex-col gap-1 min-w-[10rem]">
                                <select data-testid={`bug-status-${b.id}`}
                                        value={b.status}
                                        onChange={(e) => setStatus(b, e.target.value)}
                                        className="h-8 text-xs px-2 rounded border border-neutral-300">
                                    {BUG_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                                </select>
                                <button data-testid={`bug-link-${b.id}`}
                                        onClick={() => setLinking(b)}
                                        className="h-8 px-2 rounded border border-neutral-300 text-xs hover:bg-neutral-50 inline-flex items-center justify-center gap-1">
                                    <Plus className="h-3 w-3" /> {i18n.t("admin.l.Add_affected", "Add affected")}
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {creating && (
                <BugReportEditor
                    companies={companies}
                    onClose={() => setCreating(false)}
                    onDone={async () => { setCreating(false); await load(); }} />
            )}
            {linking && (
                <LinkCustomerDialog
                    title={i18n.t("admin.l.Add_an_affected_customer", "Add an affected customer")}
                    companies={companies}
                    endpoint={`/admin/bug-reports/${linking.id}/add-affected`}
                    onClose={() => setLinking(null)}
                    onDone={async () => { setLinking(null); await load(); }} />
            )}
        </div>
    );
}

function BugReportEditor({ companies, onClose, onDone }) {
    const [form, setForm] = useState({
        title: "", description: "", module: "",
        severity: "medium", status: "new",
        source_company_id: companies[0]?.id || "",
        source_reported_by: "", assigned_developer: "", target_release: "",
    });
    const [busy, setBusy] = useState(false);
    const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const canSave = form.title.trim().length > 0;
    const submit = async () => {
        setBusy(true);
        try {
            await api.post("/admin/bug-reports", form);
            toast.success(i18n.t("admin.l.Bug_report_created", "Bug report created"));
            await onDone();
        } catch (e) {
            toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Save_failed", "Save failed")));
        } finally { setBusy(false); }
    };
    return (
        <Drawer title={i18n.t("admin.l.New_bug_report", "New bug report")} testid="bug-editor" onClose={onClose}>
            <div className="space-y-3 text-sm">
                <Field label={i18n.t("admin.l.Title", "Title")}>
                    <input data-testid="bug-form-title" value={form.title}
                        onChange={(e) => setF("title", e.target.value)}
                        className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                </Field>
                <Field label={i18n.t("admin.l.Description", "Description")}>
                    <textarea value={form.description}
                        onChange={(e) => setF("description", e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 rounded-md border border-neutral-300" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label={i18n.t("admin.l.Module", "Module")}>
                        <input value={form.module}
                            onChange={(e) => setF("module", e.target.value)}
                            className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                    </Field>
                    <Field label={i18n.t("admin.l.Severity", "Severity")}>
                        <select data-testid="bug-form-severity"
                                value={form.severity}
                                onChange={(e) => setF("severity", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300">
                            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>
                    <Field label={i18n.t("admin.l.Source_customer", "Source customer")}>
                        <select data-testid="bug-form-company"
                                value={form.source_company_id}
                                onChange={(e) => setF("source_company_id", e.target.value)}
                                className="h-10 w-full px-3 rounded-md border border-neutral-300">
                            <option value="">{i18n.t("admin.l.none", "— none —")}</option>
                            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </Field>
                    <Field label={i18n.t("admin.l.Reported_by", "Reported by")}>
                        <input value={form.source_reported_by}
                            onChange={(e) => setF("source_reported_by", e.target.value)}
                            className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                    </Field>
                    <Field label={i18n.t("admin.l.Assigned_developer", "Assigned developer")}>
                        <input value={form.assigned_developer}
                            onChange={(e) => setF("assigned_developer", e.target.value)}
                            className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                    </Field>
                    <Field label={i18n.t("admin.l.Target_release", "Target release")}>
                        <input value={form.target_release}
                            onChange={(e) => setF("target_release", e.target.value)}
                            className="h-10 w-full px-3 rounded-md border border-neutral-300" />
                    </Field>
                </div>
            </div>
            <DrawerFooter
                onCancel={onClose}
                onSave={submit}
                saveTestid="bug-form-save"
                disabled={busy || !canSave} />
        </Drawer>
    );
}

// ============================================================ Training
function TrainingTab() {
    const [companies, setCompanies] = useState([]);
    const [companyId, setCompanyId] = useState("");
    const [matrix, setMatrix] = useState([]);
    const [busy, setBusy] = useState(false);

    const loadCompanies = useCallback(async () => {
        const { data } = await api.get("/admin/companies");
        setCompanies(data || []);
        if (data?.length) setCompanyId((cur) => cur || data[0].id);
    }, []);
    const loadMatrix = async (cid) => {
        if (!cid) return;
        const { data } = await api.get("/admin/training", { params: { company_id: cid } });
        setMatrix(data || []);
    };
    useEffect(() => { loadCompanies(); }, [loadCompanies]);
    useEffect(() => { loadMatrix(companyId); }, [companyId]);

    const seed = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/training/bulk-seed/${companyId}`);
            toast.success(i18n.t("admin.l.Training_matrix_initialised", "Training matrix initialised"));
            await loadMatrix(companyId);
        } catch (e) { toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Seed_failed", "Seed failed"))); }
        finally { setBusy(false); }
    };
    const update = async (role, course, status) => {
        try {
            await api.post("/admin/training/upsert", {
                company_id: companyId, role, course, status,
            });
            await loadMatrix(companyId);
        } catch (e) { toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Update_failed", "Update failed"))); }
    };

    const cell = (role, course) =>
        matrix.find((m) => m.role === role && m.course === course);

    return (
        <div data-testid="admin-training">
            <div className="kop-card p-4 mb-4 flex flex-wrap gap-3 items-end">
                <div>
                    <label className="text-[10px] uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Company", "Company")}</label>
                    <select data-testid="training-company-select"
                            value={companyId}
                            onChange={(e) => setCompanyId(e.target.value)}
                            className="mt-1 h-9 px-3 rounded-md border border-neutral-300 text-sm min-w-[16rem]">
                        {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <button data-testid="training-seed"
                        onClick={seed}
                        disabled={busy || !companyId}
                        className="h-9 px-3 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50 disabled:opacity-50">
                    {i18n.t("admin.l.Initialise_matrix", "Initialise matrix")}
                </button>
            </div>

            {matrix.length === 0 ? (
                <div className="kop-card p-6 text-center text-sm text-neutral-500" data-testid="training-empty">
                    {i18n.t("admin.trainingEmptyBefore", "No training records yet. Click")} <em>{i18n.t("admin.l.Initialise_matrix", "Initialise matrix")}</em> {i18n.t("admin.trainingEmptyAfter", "to seed all role × course combinations.")}
                </div>
            ) : (
                <div className="kop-card overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                            <tr>
                                <th className="text-left px-4 py-2">{i18n.t("admin.l.Course", "Course")}</th>
                                {TRAINING_ROLES_F.map((r) => (
                                    <th key={r} className="text-left px-4 py-2 capitalize">{r.replace("_", " ")}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {TRAINING_COURSES_F.map((c) => (
                                <tr key={c} className="border-t border-neutral-200">
                                    <td className="px-4 py-2 font-medium capitalize">{c.replace("_", " ")}</td>
                                    {TRAINING_ROLES_F.map((r) => {
                                        const m = cell(r, c);
                                        return (
                                            <td key={r} className="px-4 py-2">
                                                <select
                                                    data-testid={`training-cell-${c}-${r}`}
                                                    value={m?.status || "not_started"}
                                                    onChange={(e) => update(r, c, e.target.value)}
                                                    className="h-8 text-xs px-2 rounded border border-neutral-300 w-full">
                                                    {TRAINING_STATUSES_F.map((s) => (
                                                        <option key={s} value={s}>{s.replace("_", " ")}</option>
                                                    ))}
                                                </select>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ============================================================ Go-Live
function GoLiveTab() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/go-live-checklist");
            setData(data);
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); }, []);
    if (!data) return <div className="text-sm text-neutral-500">{i18n.t("admin.l.Loading", "Loading…")}</div>;
    return (
        <div data-testid="admin-go-live">
            <div className={`kop-card p-6 mb-4 flex items-center justify-between ${
                data.overall_ready ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"
            } border`}>
                <div>
                    <div className="text-xs uppercase tracking-wide text-neutral-600">{i18n.t("admin.l.Go_Live_Verdict", "Go-Live Verdict")}</div>
                    <div className="text-2xl font-bold mt-1" data-testid="go-live-verdict">
                        {data.verdict}
                    </div>
                </div>
                <button data-testid="go-live-refresh"
                        onClick={load}
                        disabled={busy}
                        className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-white">
                    {i18n.t("admin.l.Re_run_checks", "Re-run checks")}
                </button>
            </div>
            <ul className="space-y-2">
                {data.checks.map((c, idx) => (
                    <li key={idx}
                        data-testid={`go-live-check-${c.key}`}
                        className="kop-card p-3 flex items-start gap-3">
                        <div className={`mt-1 h-3 w-3 rounded-full ${
                            c.ok ? "bg-emerald-500" : "bg-rose-500"
                        }`} />
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold capitalize">{c.key.replace(/_/g, " ")}</div>
                            <div className="text-xs text-neutral-500 mt-0.5">{c.detail}</div>
                        </div>
                        <span className={`text-xs font-semibold uppercase ${
                            c.ok ? "text-emerald-700" : "text-rose-700"
                        }`}>{c.ok ? "PASS" : "FAIL"}</span>
                    </li>
                ))}
            </ul>
            <div className="text-[11px] text-neutral-500 mt-4">
                {i18n.t("admin.computedAt", { defaultValue: "Computed at {{ts}}", ts: (data.computed_at || "").slice(0, 19).replace("T", " ") })}
            </div>
        </div>
    );
}

// ============================================================ Shared
function Drawer({ title, testid, onClose, children }) {
    return (
        <div className="fixed inset-0 z-50" data-testid={testid}>
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
                <header className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
                    <div className="font-semibold">{title}</div>
                    <button onClick={onClose} className="h-8 w-8 rounded hover:bg-neutral-100 grid place-items-center">
                        <X className="h-4 w-4" />
                    </button>
                </header>
                <div className="flex-1 overflow-y-auto p-6">{children}</div>
            </aside>
        </div>
    );
}

function DrawerFooter({ onCancel, onSave, saveTestid, disabled }) {
    return (
        <div className="mt-6 flex justify-end gap-2">
            <button onClick={onCancel} className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold">
                {i18n.t("admin.l.Cancel", "Cancel")}
            </button>
            <button data-testid={saveTestid} onClick={onSave} disabled={disabled}
                    className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50">
                {i18n.t("admin.l.Save", "Save")}
            </button>
        </div>
    );
}

function LinkCustomerDialog({ title, companies, endpoint, onClose, onDone }) {
    const [companyId, setCompanyId] = useState(companies[0]?.id || "");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!companyId) return;
        setBusy(true);
        try {
            await api.post(endpoint, { company_id: companyId });
            toast.success(i18n.t("admin.l.Customer_linked", "Customer linked"));
            await onDone();
        } catch (e) {
            toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Link_failed", "Link failed")));
        } finally { setBusy(false); }
    };
    return (
        <Drawer title={title} testid="link-customer-dialog" onClose={onClose}>
            <Field label={i18n.t("admin.l.Customer", "Customer")}>
                <select data-testid="link-customer-select"
                        value={companyId}
                        onChange={(e) => setCompanyId(e.target.value)}
                        className="h-10 w-full px-3 rounded-md border border-neutral-300">
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </Field>
            <DrawerFooter onCancel={onClose} onSave={submit}
                          saveTestid="link-customer-save" disabled={busy} />
        </Drawer>
    );
}

// keep the module side-effect-free for the App.test.js smoke
export const __TABS = TABS;

// ─────────────────────────────────────────────────────────────────
// Sales Demo Tenant — Super Admin controls for the Nordic Table
// Group isolated demo company. Excluded from all commercial KPIs.
// ─────────────────────────────────────────────────────────────────
function DemoTab() {
    const [state, setState] = useState({ loading: true, exists: false });
    const [busy, setBusy] = useState(null);       // "init" | "reset" | "pw:sales" | "pw:chef"
    const [secret, setSecret] = useState(null);   // { title, lines: [{label,value}] }
    const [confirmReset, setConfirmReset] = useState(false);
    const [resetTyped, setResetTyped] = useState("");

    const load = async () => {
        setState((s) => ({ ...s, loading: true }));
        try {
            const { data } = await api.get("/admin/demo");
            setState({ loading: false, ...data });
        } catch (err) {
            toast.error(apiErrorMessage(err) || err.message);
            setState({ loading: false, exists: false });
        }
    };
    useEffect(() => { load(); }, []);

    const initDemo = async () => {
        setBusy("init"); setSecret(null);
        try {
            const { data } = await api.post("/admin/demo/init");
            toast.success(data.already_existed
                ? i18n.t("admin.l.Demo_tenant_already_exists_nothing_to_create", "Demo tenant already exists — nothing to create.")
                : i18n.t("admin.l.Demo_tenant_created", "Demo tenant created."));
            if (!data.already_existed) {
                setSecret({
                    get title() { return i18n.t("admin.l.Temporary_demo_passwords_copy_now_they_won_t_be_shown_again", "Temporary demo passwords — copy now, they won't be shown again"); },
                    lines: [
                        { label: data.users.sales.email, value: data.users.sales.temporary_password },
                        { label: data.users.chef.email,  value: data.users.chef.temporary_password  },
                    ],
                });
            }
            await load();
        } catch (err) {
            toast.error(apiErrorMessage(err) || err.message);
        } finally { setBusy(null); }
    };

    const resetDemo = async () => {
        if (resetTyped !== "RESET DEMO") return;
        setBusy("reset"); setSecret(null);
        try {
            const { data } = await api.post("/admin/demo/reset",
                { confirm: "RESET DEMO", seed_content: true });
            toast.success(i18n.t("admin.l.Demo_data_reset_and_re_seeded", "Demo data reset and re-seeded."));
            setSecret({
                get title() { return i18n.t("admin.l.New_demo_passwords_copy_now_they_won_t_be_shown_again", "New demo passwords — copy now, they won't be shown again"); },
                lines: [
                    { label: data.users.sales.email, value: data.users.sales.temporary_password },
                    { label: data.users.chef.email,  value: data.users.chef.temporary_password  },
                ],
            });
            setConfirmReset(false); setResetTyped("");
            await load();
        } catch (err) {
            toast.error(apiErrorMessage(err) || err.message);
        } finally { setBusy(null); }
    };

    const resetPassword = async (userKey) => {
        setBusy(`pw:${userKey}`); setSecret(null);
        try {
            const { data } = await api.post("/admin/demo/reset-password",
                { user_key: userKey });
            setSecret({
                title: i18n.t("admin.newPasswordFor", { defaultValue: "New password for {{email}} — copy now, it won't be shown again", email: data.email, interpolation: { escapeValue: false } }),
                lines: [{ label: data.email, value: data.temporary_password }],
            });
            toast.success(i18n.t("admin.l.Password_reset", "Password reset."));
        } catch (err) {
            toast.error(apiErrorMessage(err) || err.message);
        } finally { setBusy(null); }
    };

    const copyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(i18n.t("admin.l.Copied", "Copied"));
        } catch { /* ignore */ }
    };

    if (state.loading) {
        return <div className="text-sm text-neutral-500" data-testid="demo-loading">{i18n.t("admin.l.Loading_demo_tenant", "Loading demo tenant…")}</div>;
    }

    if (!state.exists) {
        return (
            <div className="kop-card p-8 text-center max-w-lg mx-auto" data-testid="demo-empty">
                <Rocket className="h-8 w-8 mx-auto text-neutral-400 mb-3" />
                <div className="text-lg font-semibold mb-1">{i18n.t("admin.l.No_sales_demo_yet", "No sales demo yet")}</div>
                <div className="text-sm text-neutral-500 mb-6">
                    <span dangerouslySetInnerHTML={{ __html: i18n.t("admin.demoIntro") }} />
                </div>
                <button onClick={initDemo} disabled={busy === "init"}
                        data-testid="demo-init-btn"
                        className="h-10 px-5 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-50">
                    {busy === "init" ? i18n.t("admin.l.Creating", "Creating…") : i18n.t("admin.l.Initialise_Sales_Demo", "Initialise Sales Demo")}
                </button>
                {secret && <SecretBox secret={secret} onCopy={copyText} onDismiss={() => setSecret(null)} />}
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="demo-tab">
            <section className="kop-card p-6" data-testid="demo-summary">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-1">
                            {i18n.t("admin.l.Demo_company", "Demo company")}
                        </div>
                        <div className="text-lg font-semibold">{state.company_name}</div>
                        <div className="text-xs text-neutral-500 mt-1">
                            ID · <code className="font-mono">{state.company_id}</code>
                        </div>
                    </div>
                    <a href={`/admin/companies/${state.company_id}`}
                        data-testid="demo-open-company"
                        className="h-9 px-4 inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white text-sm font-semibold hover:bg-neutral-50">
                        <Play className="h-4 w-4" /> {i18n.t("admin.l.Open_Demo_Company", "Open Demo Company")}
                    </a>
                </div>
                <div className="mt-4 grid sm:grid-cols-3 gap-3">
                    {state.restaurants?.map((r) => (
                        <div key={r.id} className="rounded-md border border-neutral-200 bg-white p-3">
                            <div className="text-[11px] uppercase tracking-wide text-neutral-500">{i18n.t("admin.l.Restaurant", "Restaurant")}</div>
                            <div className="text-sm font-semibold">{r.name}</div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="kop-card p-6" data-testid="demo-users">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-3">
                    Demo users (visible only to Super Admin)
                </div>
                <div className="space-y-3">
                    {(state.users || []).map((u) => (
                        <div key={u.key}
                             data-testid={`demo-user-${u.key}`}
                             className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-neutral-200 bg-white p-3">
                            <div className="min-w-0">
                                <div className="text-sm font-semibold truncate">{u.email}</div>
                                <div className="text-xs text-neutral-500">
                                    {u.key === "sales" ? i18n.t("admin.l.Corporate_all_restaurants", "Corporate · all restaurants") :
                                     i18n.t("admin.headChefAt", { defaultValue: "Head Chef · {{name}}", name: u.restaurant_name || "—", interpolation: { escapeValue: false } })}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => copyText(u.email)}
                                        data-testid={`demo-copy-email-${u.key}`}
                                        className="h-8 px-3 inline-flex items-center gap-1 rounded-md border border-neutral-300 bg-white text-xs font-semibold hover:bg-neutral-50">
                                    <CopyIcon className="h-3.5 w-3.5" /> {i18n.t("admin.l.Copy_email", "Copy email")}
                                </button>
                                <button onClick={() => resetPassword(u.key)}
                                        disabled={busy === `pw:${u.key}`}
                                        data-testid={`demo-reset-password-${u.key}`}
                                        className="h-8 px-3 inline-flex items-center gap-1 rounded-md bg-neutral-900 text-white text-xs font-semibold hover:bg-neutral-800 disabled:opacity-50">
                                    <KeyRound className="h-3.5 w-3.5" /> {i18n.t("admin.l.Reset_password", "Reset password")}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="kop-card p-6" data-testid="demo-danger">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-red-700 mb-2">
                    {i18n.t("admin.l.Danger_zone", "Danger zone")}
                </div>
                <div className="text-sm text-neutral-700 mb-3">
                    <span dangerouslySetInnerHTML={{ __html: i18n.t("admin.resetWarning") }} />
                </div>
                {!confirmReset ? (
                    <button onClick={() => setConfirmReset(true)}
                            data-testid="demo-reset-btn"
                            className="h-9 px-4 inline-flex items-center gap-2 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700">
                        <Trash2 className="h-4 w-4" /> {i18n.t("admin.l.Reset_Demo_Data", "Reset Demo Data")}
                    </button>
                ) : (
                    <div className="rounded-md border border-red-300 bg-red-50 p-3 space-y-2">
                        <div className="text-sm">
                            {i18n.t("admin.typeToConfirm", "Type")} <code className="font-mono font-bold">RESET DEMO</code> {i18n.t("admin.toConfirm", "to confirm:")}
                        </div>
                        <input value={resetTyped} onChange={(e) => setResetTyped(e.target.value)}
                               data-testid="demo-reset-input"
                               autoFocus
                               className="w-full h-9 rounded-md border border-neutral-300 px-3 font-mono text-sm bg-white" />
                        <div className="flex items-center gap-2">
                            <button onClick={resetDemo}
                                    disabled={resetTyped !== "RESET DEMO" || busy === "reset"}
                                    data-testid="demo-reset-confirm"
                                    className="h-9 px-4 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
                                {busy === "reset" ? i18n.t("admin.l.Resetting", "Resetting…") : i18n.t("admin.l.Confirm_reset", "Confirm reset")}
                            </button>
                            <button onClick={() => { setConfirmReset(false); setResetTyped(""); }}
                                    data-testid="demo-reset-cancel"
                                    className="h-9 px-4 rounded-md border border-neutral-300 bg-white text-sm">
                                {i18n.t("admin.l.Cancel", "Cancel")}
                            </button>
                        </div>
                    </div>
                )}
            </section>

            {secret && <SecretBox secret={secret} onCopy={copyText} onDismiss={() => setSecret(null)} />}
        </div>
    );
}

function SecretBox({ secret, onCopy, onDismiss }) {
    return (
        <div className="kop-card p-4 border-2 border-amber-300 bg-amber-50" data-testid="demo-secret-box">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="text-sm font-semibold text-amber-900">{secret.title}</div>
                <button onClick={onDismiss}
                        data-testid="demo-secret-dismiss"
                        className="h-7 w-7 grid place-items-center rounded hover:bg-amber-100">
                    <X className="h-4 w-4" />
                </button>
            </div>
            <div className="space-y-2">
                {secret.lines.map((l, i) => (
                    <div key={i} className="flex items-center gap-2 bg-white rounded-md border border-amber-200 p-2">
                        <div className="text-xs text-neutral-500 min-w-0 truncate">{l.label}</div>
                        <code data-testid={`demo-secret-value-${i}`}
                              className="ml-auto font-mono text-sm font-bold">{l.value}</code>
                        <button onClick={() => onCopy(l.value)}
                                data-testid={`demo-secret-copy-${i}`}
                                className="h-7 w-7 grid place-items-center rounded hover:bg-neutral-100">
                            <CopyIcon className="h-3.5 w-3.5" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────
// Commercial / Sales Overview — placeholder for future revenue funnel
// (MRR, conversion, pipeline). Data lives elsewhere; this surface just
// gives the platform owner a single dashboard entry point.
// ─────────────────────────────────────────────────────────────────
function CommercialTab() {
    const [stats, setStats] = useState(null);
    const navigate = useNavigate();
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/dashboard");
                setStats(data);
            } catch { /* placeholder */ }
        })();
    }, []);
    const lc = stats?.lifecycle_counts || {};
    // Month Revenue — SUM(paid invoices this calendar month) in EUR.
    // Only status="paid" counts. Draft / ready / sent / overdue / cancelled
    // are excluded by the backend query. Value refreshes on any Mark Paid.
    const monthRevenue = Number(stats?.month_revenue || 0);
    const currency = stats?.month_revenue_currency || "EUR";
    const revenueFormatted = new Intl.NumberFormat("en-IE", {
        style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(monthRevenue);
    const cards = [
        { key: "leads",           get label() { return i18n.t("admin.l.Leads", "Leads"); },           value: lc.lead || 0 },
        { key: "demos",           get label() { return i18n.t("admin.l.Demos_scheduled", "Demos scheduled"); }, value: lc.demo_scheduled || 0 },
        { key: "pilots",          get label() { return i18n.t("admin.l.Pilots", "Pilots"); },          value: lc.pilot || 0 },
        { key: "trials",          get label() { return i18n.t("admin.l.Trials", "Trials"); },          value: lc.trial || 0 },
        { key: "active",          get label() { return i18n.t("admin.l.Active_customers", "Active customers"); },value: lc.active_customer || 0 },
        { key: "pending_payment", get label() { return i18n.t("admin.l.Pending_payment", "Pending payment"); }, value: lc.pending_payment || 0 },
    ];
    return (
        <div data-testid="admin-commercial-tab" className="space-y-6">
            <button
                type="button"
                data-testid="commercial-stat-month_revenue"
                onClick={() => navigate("/admin?tab=invoices")}
                title={i18n.t("admin.l.Open_Invoices", "Open Invoices")}
                className="w-full text-left rounded-lg border border-neutral-200 bg-white p-4 hover:bg-neutral-50 transition-colors cursor-pointer">
                <div className="text-xs uppercase tracking-wide text-neutral-500">
                    {i18n.t("admin.l.Month_Revenue", "Month Revenue")}
                </div>
                <div className="text-3xl font-bold mt-1" data-testid="commercial-month-revenue-value">
                    {revenueFormatted}
                </div>
            </button>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {cards.map((c) => (
                    <div key={c.key}
                         data-testid={`commercial-stat-${c.key}`}
                         className="rounded-lg border border-neutral-200 bg-white p-4">
                        <div className="text-xs uppercase tracking-wide text-neutral-500">{c.label}</div>
                        <div className="text-3xl font-bold mt-1">{c.value}</div>
                    </div>
                ))}
            </div>
            <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-6 text-sm text-neutral-600">
                <div className="font-semibold text-neutral-900 mb-2">
                    {i18n.t("admin.l.Sales_and_Revenue_Overview_coming_next", "Sales & Revenue Overview — coming next")}
                </div>
                <p>
                    {i18n.t("admin.commercialComing", "Full commercial dashboard (MRR, ARR, churn, expansion revenue, pipeline conversion by stage, sales rep leaderboard) will land here once Stripe billing goes live. For now, use the Lifecycle, Subscriptions and Go-Live tabs to follow every customer end-to-end.")}
                </p>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────
// Platform Settings — business-only settings for MiseOne owner.
// Read-only placeholder cards for MVP: branding, support routing,
// plan defaults, notification preferences, provider slots for future
// billing/email integrations. Never mixes with restaurant kitchen
// settings (which live on the restaurant-facing /settings page).
// ─────────────────────────────────────────────────────────────────
function PlatformSettingsTab() {
    const navigate = useNavigate();
    const groups = [
        {
            key: "branding", get title() { return i18n.t("admin.l.Platform_Branding", "Platform Branding"); },
            get hint() { return i18n.t("admin.l.Logo_colours_and_marketing_name_used_across_the_customer_portal", "Logo, colours and marketing name used across the customer portal."); },
            fields: [
                { get label() { return i18n.t("admin.l.Product_name", "Product name"); }, value: "MiseOne" },
                { get label() { return i18n.t("admin.l.Tagline", "Tagline"); }, value: "Less work. More cooking." },
                { get label() { return i18n.t("admin.l.Support_email", "Support email"); }, value: "support@miseone.app" },
            ],
        },
        {
            key: "support", get title() { return i18n.t("admin.l.Support_Settings", "Support Settings"); },
            get hint() { return i18n.t("admin.l.Routing_and_defaults_for_customer_support_notes", "Routing and defaults for customer support notes."); },
            fields: [
                { get label() { return i18n.t("admin.l.Default_priority", "Default priority"); }, value: "medium" },
                { get label() { return i18n.t("admin.l.Default_owner", "Default owner"); }, value: "platform-team" },
                { get label() { return i18n.t("admin.l.Follow_up_SLA_business_days", "Follow-up SLA (business days)"); }, value: "3" },
            ],
        },
        {
            key: "plans", get title() { return i18n.t("admin.l.Plan_Defaults", "Plan Defaults"); },
            get hint() { return i18n.t("admin.l.Defaults_applied_when_creating_a_new_subscription", "Defaults applied when creating a new subscription."); },
            fields: [
                { get label() { return i18n.t("admin.l.Trial_duration_days", "Trial duration (days)"); }, value: "14" },
                { get label() { return i18n.t("admin.l.Default_plan", "Default plan"); }, value: "starter" },
                { get label() { return i18n.t("admin.l.Grace_period_days", "Grace period (days)"); }, value: "7" },
            ],
        },
        {
            key: "notifications", get title() { return i18n.t("admin.l.Notification_Settings", "Notification Settings"); },
            get hint() { return i18n.t("admin.l.How_the_platform_surfaces_platform_level_alerts", "How the platform surfaces platform-level alerts."); },
            fields: [
                { get label() { return i18n.t("admin.l.Trial_ending_threshold_days", "Trial-ending threshold (days)"); }, value: "7" },
                { get label() { return i18n.t("admin.l.Broadcast_channel", "Broadcast channel"); }, value: "email" },
            ],
        },
        {
            key: "billing", get title() { return i18n.t("admin.l.Billing_Provider_future", "Billing Provider (future)"); },
            get hint() { return i18n.t("admin.l.Reserved_slot_for_Stripe_manual_invoicing_configuration", "Reserved slot for Stripe / manual invoicing configuration."); },
            fields: [
                { get label() { return i18n.t("admin.l.Provider", "Provider"); }, value: "not_configured" },
                { get label() { return i18n.t("admin.l.Test_mode", "Test mode"); }, value: "n/a" },
            ],
            comingSoon: true,
        },
        {
            key: "email", get title() { return i18n.t("admin.l.Email_Provider_future", "Email Provider (future)"); },
            get hint() { return i18n.t("admin.l.Reserved_slot_for_SendGrid_Resend_configuration", "Reserved slot for SendGrid / Resend configuration."); },
            fields: [
                { get label() { return i18n.t("admin.l.Provider", "Provider"); }, value: "not_configured" },
            ],
            comingSoon: true,
        },
    ];
    return (
        <div data-testid="admin-settings-tab" className="space-y-4">
            <div className="rounded-md border border-neutral-200 bg-neutral-50 px-4 py-3 text-xs text-neutral-600">
                {i18n.t("admin.l.Platform_settings_apply_to_the_MiseOne_SaaS_business_Restaurant_kitche", "Platform settings apply to the MiseOne SaaS business. Restaurant kitchen preferences live on each restaurant's own Settings page.")}
            </div>

            {/* Platform Tools — utilities that used to live in the top menu. */}
            <div className="rounded-lg border border-neutral-200 bg-white p-5"
                 data-testid="admin-settings-card-platform-tools">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-sm font-semibold">{i18n.t("admin.l.Platform_Tools", "Platform Tools")}</div>
                        <div className="text-xs text-neutral-500 mt-0.5">
                            {i18n.t("admin.l.Utilities_used_occasionally_not_part_of_daily_business_operation", "Utilities used occasionally — not part of daily business operation.")}
                        </div>
                    </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                    <button
                        data-testid="platform-tool-go-live"
                        onClick={() => navigate("/admin?tab=go_live")}
                        className="h-10 px-4 rounded-md border border-neutral-300 hover:bg-neutral-50 text-sm font-semibold">
                        {i18n.t("admin.l.Go_Live_Readiness", "Go-Live Readiness")}
                    </button>
                    <button
                        data-testid="platform-tool-restaurants"
                        onClick={() => navigate("/admin?tab=restaurants")}
                        className="h-10 px-4 rounded-md border border-neutral-300 hover:bg-neutral-50 text-sm font-semibold">
                        All Restaurants (cross-company)
                    </button>
                    <button
                        data-testid="platform-tool-users"
                        onClick={() => navigate("/admin?tab=users")}
                        className="h-10 px-4 rounded-md border border-neutral-300 hover:bg-neutral-50 text-sm font-semibold">
                        All Users (cross-company)
                    </button>
                    <button
                        data-testid="platform-tool-alerts"
                        onClick={() => navigate("/admin?tab=notifications")}
                        className="h-10 px-4 rounded-md border border-neutral-300 hover:bg-neutral-50 text-sm font-semibold">
                        {i18n.t("admin.l.Platform_Alerts", "Platform Alerts")}
                    </button>
                </div>
            </div>

            <BarcodeDiagnosticCard />

            <div className="grid gap-4 md:grid-cols-2">
                {groups.map((g) => (
                    <div key={g.key}
                         data-testid={`admin-settings-card-${g.key}`}
                         className="rounded-lg border border-neutral-200 bg-white p-5">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="text-sm font-semibold">{g.title}</div>
                                <div className="text-xs text-neutral-500 mt-0.5">{g.hint}</div>
                            </div>
                            {g.comingSoon && (
                                <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                                    {i18n.t("admin.l.Coming_soon", "Coming soon")}
                                </span>
                            )}
                        </div>
                        <dl className="mt-4 text-sm space-y-2">
                            {g.fields.map((f, i) => (
                                <div key={i} className="flex justify-between items-baseline border-b border-neutral-100 pb-1.5">
                                    <dt className="text-neutral-500 text-xs">{f.label}</dt>
                                    <dd className="font-medium text-neutral-900">{f.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                ))}
            </div>
        </div>
    );
}



// ─────────────────────────────────────────────────────────────
// BarcodeDiagnosticCard — Prompt 31.
// Shows the current barcode lookup chain status: internal DB,
// GS1 Finland (configured / not configured), Open Food Facts, plus
// the last lookup event. Read-only diagnostic surface.
// ─────────────────────────────────────────────────────────────
function BarcodeDiagnosticCard() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/admin/barcode-diagnostic")
            .then(({ data }) => setData(data))
            .catch(() => setData({ providers: [], last_lookup: null }));
    }, []);
    if (!data) return null;
    const statusTone = (s) => {
        if (s === "active" || s === "configured" || s === "ok") return "bg-emerald-50 text-emerald-700 border-emerald-200";
        if (s === "not_found") return "bg-neutral-100 text-neutral-700 border-neutral-200";
        return "bg-amber-50 text-amber-800 border-amber-200";
    };
    return (
        <div className="rounded-lg border border-neutral-200 bg-white p-5"
             data-testid="admin-settings-card-barcode">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-sm font-semibold">{i18n.t("admin.l.Barcode_Lookup_Providers", "Barcode Lookup Providers")}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                        {i18n.t("admin.l.Chain_used_by_Inventory_and_Delivery_barcode_scans_Prices_are_never_im", "Chain used by Inventory & Delivery barcode scans. Prices are never imported — they always come from confirmed Deliveries.")}
                    </div>
                </div>
            </div>
            <ul className="mt-4 space-y-2" data-testid="barcode-providers-list">
                {(data.providers || []).map((p) => (
                    <li key={p.name}
                        data-testid={`barcode-provider-${p.name}`}
                        className="flex items-center justify-between text-sm border-b border-neutral-100 pb-2 last:border-0">
                        <div>
                            <div className="font-medium">{p.label}</div>
                            <div className="text-xs text-neutral-500">
                                {i18n.t("admin.priorityN", { defaultValue: "Priority {{n}}", n: p.priority })}
                            </div>
                        </div>
                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${statusTone(p.status)}`}
                              data-testid={`barcode-provider-${p.name}-status`}>
                            {p.status.replace(/_/g, " ")}
                        </span>
                    </li>
                ))}
            </ul>
            {data.last_lookup && data.last_lookup.at && (
                <div className="mt-4 rounded-md bg-neutral-50 border border-neutral-100 px-3 py-2 text-xs text-neutral-600"
                     data-testid="barcode-last-lookup">
                    <div>
                        <strong>{i18n.t("admin.lastLookup", "Last lookup:")}</strong> {data.last_lookup.barcode} ·{" "}
                        {data.last_lookup.provider} · {data.last_lookup.status}
                    </div>
                    {data.last_lookup.error && (
                        <div className="text-rose-600 mt-1">
                            {i18n.t("admin.errorLabel", { defaultValue: "Error: {{e}}", e: data.last_lookup.error, interpolation: { escapeValue: false } })}
                        </div>
                    )}
                    <div className="text-neutral-400 mt-1">
                        {new Date(data.last_lookup.at).toLocaleString("en-US")}
                    </div>
                </div>
            )}
        </div>
    );
}


// ─────────────────────────────────────────────────────────────
// Launch Fix R7 — Invoices tab (Super Admin only)
// ─────────────────────────────────────────────────────────────
const STATUS_LABEL = {
    draft:     { get text() { return i18n.t("admin.l.Draft", "Draft"); },     tone: "low" },
    ready:     { get text() { return i18n.t("admin.l.Ready", "Ready"); },     tone: "pending" },
    sent:      { get text() { return i18n.t("admin.l.Sent", "Sent"); },      tone: "pending" },
    paid:      { get text() { return i18n.t("admin.l.Paid", "Paid"); },      tone: "completed" },
    overdue:   { get text() { return i18n.t("admin.l.Overdue", "Overdue"); },   tone: "critical" },
    cancelled: { get text() { return i18n.t("admin.l.Cancelled", "Cancelled"); }, tone: "low" },
    failed:    { get text() { return i18n.t("admin.l.Failed", "Failed"); },    tone: "critical" },
};

function InvoicesTab() {
    const [rows, setRows] = useState([]);
    const [filters, setFilters] = useState({ status: "", company: "", plan: "", q: "" });
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [sending, setSending] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data } = await api.get("/admin/invoices", { params });
            setRows(data);
        } finally { setLoading(false); }
    }, [filters]);
    useEffect(() => { load(); }, [load]);

    const openPdf = (id) => {
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/${id}/pdf`,
                     "_blank", "noopener");
    };
    const downloadCsv = () => {
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/export.csv`,
                     "_blank", "noopener");
    };
    const markPaid = async (inv) => {
        const ref = window.prompt(i18n.t("admin.l.Payment_reference_optional", "Payment reference (optional):"), inv.invoice_number);
        if (ref === null) return;
        try {
            await api.post(`/admin/invoices/${inv.id}/mark-paid`, { payment_reference: ref });
            toast.success(i18n.t("admin.l.Marked_as_paid", "Marked as paid")); load();
        } catch (e) { toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Action_failed", "Action failed"))); }
    };
    const markUnpaid = async (inv) => {
        if (!window.confirm(i18n.t("admin.l.Revert_this_invoice_to_unpaid_status", "Revert this invoice to unpaid status?"))) return;
        try {
            await api.post(`/admin/invoices/${inv.id}/mark-unpaid`, {});
            toast.success(i18n.t("admin.l.Marked_as_unpaid", "Marked as unpaid")); load();
        } catch (e) { toast.error(i18n.t("admin.l.Action_failed", "Action failed")); }
    };
    const duplicate = async (inv) => {
        try {
            const { data } = await api.post(`/admin/invoices/${inv.id}/duplicate`, {});
            toast.success(i18n.t("admin.duplicatedAs", { defaultValue: "Duplicated as {{n}}", n: data.invoice_number }));
            load();
        } catch (e) { toast.error(i18n.t("admin.l.Duplicate_failed", "Duplicate failed")); }
    };

    return (
        <div data-testid="admin-invoices">
            <div className="mb-4 flex flex-wrap gap-2 items-center">
                <input
                    data-testid="invoices-search"
                    value={filters.q}
                    onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                    placeholder={i18n.t("admin.l.Search_company_plan", "Search # / company / plan")}
                    className="h-9 px-3 rounded-md border border-neutral-300 text-sm min-w-64"
                />
                <select
                    data-testid="invoices-filter-status"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    className="h-9 px-3 rounded-md border border-neutral-300 text-sm"
                >
                    <option value="">{i18n.t("admin.l.All_statuses", "All statuses")}</option>
                    {Object.entries(STATUS_LABEL).map(([k, v]) =>
                        <option key={k} value={k}>{v.text}</option>)}
                </select>
                <div className="ml-auto flex items-center gap-2">
                    <button
                        data-testid="invoices-export-csv"
                        onClick={downloadCsv}
                        className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50"
                    ><Download className="h-4 w-4" /> CSV</button>
                    <button
                        data-testid="invoices-new"
                        onClick={() => setCreating(true)}
                        className="h-9 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold"
                    ><Plus className="h-4 w-4" /> {i18n.t("admin.l.New_invoice", "New invoice")}</button>
                </div>
            </div>
            <div className="kop-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                        <tr>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Invoice", "Invoice #")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Company", "Company")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Plan", "Plan")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Period", "Period")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Issue", "Issue")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Due", "Due")}</th>
                            <th className="text-right px-4 py-2">{i18n.t("admin.l.Total", "Total")}</th>
                            <th className="text-left px-4 py-2">{i18n.t("admin.l.Status", "Status")}</th>
                            <th className="px-4 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan="9" className="px-4 py-6 text-center text-neutral-500">{i18n.t("admin.l.Loading", "Loading…")}</td></tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan="9" className="px-4 py-6 text-center text-neutral-500">{i18n.t("admin.l.No_invoices_yet", "No invoices yet.")}</td></tr>
                        )}
                        {rows.map((r) => {
                            const st = STATUS_LABEL[r.effective_status || r.status] || { text: r.status, tone: "low" };
                            return (
                                <tr key={r.id} className="border-t border-neutral-200"
                                    data-testid={`invoice-row-${r.invoice_number}`}>
                                    <td className="px-4 py-2 font-mono">{r.invoice_number}</td>
                                    <td className="px-4 py-2 font-medium">{r.company_snapshot?.name || "—"}</td>
                                    <td className="px-4 py-2 capitalize">{r.plan_code || "—"}</td>
                                    <td className="px-4 py-2 text-xs text-neutral-600">{r.period_from} → {r.period_to}</td>
                                    <td className="px-4 py-2">{r.issue_date}</td>
                                    <td className="px-4 py-2">{r.due_date}</td>
                                    <td className="px-4 py-2 text-right tabular-nums">
                                        €{Number(r.total || 0).toFixed(2)}
                                        <div className="text-[10px] text-neutral-500">VAT {Number(r.vat_pct || 0).toFixed(1)}%</div>
                                    </td>
                                    <td className="px-4 py-2">
                                        <StatusBadge status={st.tone}>{st.text}</StatusBadge>
                                    </td>
                                    <td className="px-4 py-2 text-right whitespace-nowrap">
                                        <button title="PDF" onClick={() => openPdf(r.id)}
                                            data-testid={`invoice-pdf-${r.invoice_number}`}
                                            className="h-8 w-8 inline-flex items-center justify-center rounded border border-neutral-300 hover:bg-neutral-50 mr-1"
                                        ><FileText className="h-3.5 w-3.5" /></button>
                                        <button title={i18n.t("admin.l.Send_email", "Send email")} onClick={() => setSending(r)}
                                            data-testid={`invoice-send-${r.invoice_number}`}
                                            className="h-8 w-8 inline-flex items-center justify-center rounded border border-neutral-300 hover:bg-neutral-50 mr-1"
                                        ><Mail className="h-3.5 w-3.5" /></button>
                                        <button title={i18n.t("admin.l.Edit", "Edit")} onClick={() => setEditing(r)}
                                            data-testid={`invoice-edit-${r.invoice_number}`}
                                            className="h-8 w-8 inline-flex items-center justify-center rounded border border-neutral-300 hover:bg-neutral-50 mr-1"
                                        ><Edit2 className="h-3.5 w-3.5" /></button>
                                        <button title={i18n.t("admin.l.Duplicate", "Duplicate")} onClick={() => duplicate(r)}
                                            className="h-8 w-8 inline-flex items-center justify-center rounded border border-neutral-300 hover:bg-neutral-50 mr-1"
                                        ><CopyIcon className="h-3.5 w-3.5" /></button>
                                        {r.status !== "paid" ? (
                                            <button
                                                data-testid={`invoice-mark-paid-${r.invoice_number}`}
                                                onClick={() => markPaid(r)}
                                                className="h-8 px-2 inline-flex items-center gap-1 rounded bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
                                            ><Check className="h-3 w-3" /> {i18n.t("admin.l.Paid", "Paid")}</button>
                                        ) : (
                                            <button onClick={() => markUnpaid(r)}
                                                className="h-8 px-2 inline-flex items-center gap-1 rounded border border-neutral-300 text-xs font-semibold hover:bg-neutral-50"
                                            >{i18n.t("admin.l.Unpaid", "Unpaid")}</button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {(creating || editing) && (
                <InvoiceEditor seed={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onDone={() => { setCreating(false); setEditing(null); load(); }} />
            )}
            {sending && (
                <SendInvoiceDialog invoice={sending}
                    onClose={() => setSending(null)}
                    onSent={() => { setSending(null); load(); }} />
            )}
        </div>
    );
}

function InvoiceEditor({ seed, onClose, onDone }) {
    const isEdit = !!seed;
    const [companies, setCompanies] = useState([]);
    const [plans, setPlans] = useState([]);
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({
        company_id: seed?.company_id || "",
        plan_code: seed?.plan_code || "",
        subscription_id: seed?.subscription_id || "",
        period_from: seed?.period_from || today.slice(0, 8) + "01",
        period_to: seed?.period_to || today,
        issue_date: seed?.issue_date || today,
        due_date: seed?.due_date || "",
        seats: seed?.seats ?? 1,
        unit_price: seed?.unit_price ?? 0,
        vat_pct: seed?.vat_pct ?? 25.5,
        discount: seed?.discount ?? 0,
        description: seed?.description ?? "MiseOne Subscription",
        notes: seed?.notes ?? "",
    });
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (!isEdit) api.get("/admin/companies").then(({ data }) => setCompanies(data));
        api.get("/admin/plans").then(({ data }) => setPlans(data));
    }, [isEdit]);
    const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const subtotal = Math.max(0, Number(form.seats || 0) * Number(form.unit_price || 0) - Number(form.discount || 0));
    const vat = subtotal * Number(form.vat_pct || 0) / 100;
    const total = subtotal + vat;

    const submit = async () => {
        setBusy(true);
        try {
            const payload = { ...form,
                seats: Number(form.seats), unit_price: Number(form.unit_price),
                vat_pct: Number(form.vat_pct), discount: Number(form.discount),
                due_date: form.due_date || null };
            if (isEdit) await api.patch(`/admin/invoices/${seed.id}`, payload);
            else await api.post("/admin/invoices", payload);
            toast.success(isEdit ? i18n.t("admin.l.Invoice_updated", "Invoice updated") : i18n.t("admin.l.Invoice_created", "Invoice created"));
            await onDone();
        } catch (e) {
            toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Save_failed", "Save failed")));
        } finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="invoice-editor" className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>{isEdit ? `Edit invoice ${seed.invoice_number}` : i18n.t("admin.l.New_invoice", "New invoice")}</DialogTitle>
                    <DialogDescription>{i18n.t("admin.l.Enter_billing_details_Totals_update_live_below", "Enter billing details. Totals update live below.")}</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    {!isEdit && (
                        <label className="block col-span-2">
                            <span className="text-xs uppercase font-semibold text-neutral-600">{i18n.t("admin.l.Company", "Company")}</span>
                            <select
                                data-testid="invoice-company"
                                value={form.company_id}
                                onChange={(e) => setF("company_id", e.target.value)}
                                className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm"
                            >
                                <option value="">{i18n.t("admin.l.Choose_company", "Choose company…")}</option>
                                {companies.map((c) =>
                                    <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </label>
                    )}
                    <label className="block">
                        <span className="text-xs uppercase font-semibold text-neutral-600">{i18n.t("admin.l.Plan", "Plan")}</span>
                        <select
                            data-testid="invoice-plan"
                            value={form.plan_code}
                            onChange={(e) => {
                                const p = plans.find((x) => x.code === e.target.value);
                                setF("plan_code", e.target.value);
                                if (p) setF("unit_price", p.monthly_price);
                            }}
                            className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm"
                        >
                            <option value="">—</option>
                            {plans.map((p) =>
                                <option key={p.code} value={p.code}>{p.name}</option>)}
                        </select>
                    </label>
                    {[
                        ["period_from", i18n.t("admin.l.Period_from", "Period from"), "date"],
                        ["period_to", i18n.t("admin.l.Period_to", "Period to"), "date"],
                        ["issue_date", i18n.t("admin.l.Issue_date", "Issue date"), "date"],
                        ["due_date", i18n.t("admin.l.Due_date_auto_if_empty", "Due date (auto if empty)"), "date"],
                        ["seats", i18n.t("admin.l.Seats", "Seats"), "number"],
                        ["unit_price", i18n.t("admin.l.Unit_price_EUR", "Unit price (€)"), "number"],
                        ["vat_pct", i18n.t("admin.l.VAT_PCT_25_5_0_custom", "VAT % (25.5, 0, custom)"), "number"],
                        ["discount", i18n.t("admin.l.Discount_EUR", "Discount (€)"), "number"],
                        ["description", i18n.t("admin.l.Description", "Description"), "text"],
                    ].map(([k, label, type]) => (
                        <label key={k} className="block">
                            <span className="text-xs uppercase font-semibold text-neutral-600">{label}</span>
                            <input
                                data-testid={`invoice-${k}`}
                                type={type} step={type === "number" ? "0.01" : undefined}
                                value={form[k] ?? ""}
                                onChange={(e) => setF(k, e.target.value)}
                                className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm"
                            />
                        </label>
                    ))}
                    <label className="block col-span-2">
                        <span className="text-xs uppercase font-semibold text-neutral-600">{i18n.t("admin.l.Notes", "Notes")}</span>
                        <textarea rows={2}
                            value={form.notes}
                            onChange={(e) => setF("notes", e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                    </label>
                </div>
                <div className="mt-4 rounded-md bg-neutral-50 border border-neutral-200 p-3 text-sm">
                    <div className="flex justify-between"><span>{i18n.t("admin.l.Subtotal_Net", "Subtotal (Net)")}</span><span className="tabular-nums">€{subtotal.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span>VAT ({Number(form.vat_pct || 0).toFixed(1)}%)</span><span className="tabular-nums">€{vat.toFixed(2)}</span></div>
                    <div className="flex justify-between font-semibold border-t border-neutral-200 mt-1 pt-1">
                        <span>{i18n.t("admin.l.Total_EUR", "Total (EUR)")}</span>
                        <span data-testid="invoice-total" className="tabular-nums text-lg">€{total.toFixed(2)}</span>
                    </div>
                </div>
                <DialogFooter>
                    <button onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold">{i18n.t("admin.l.Cancel", "Cancel")}</button>
                    <button
                        data-testid="invoice-save"
                        onClick={submit} disabled={busy || (!isEdit && !form.company_id)}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-60"
                    >{busy ? i18n.t("admin.l.Saving", "Saving…") : (isEdit ? i18n.t("admin.l.Save", "Save") : i18n.t("admin.l.Create_invoice", "Create invoice"))}</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SendInvoiceDialog({ invoice, onClose, onSent }) {
    const [recipient, setRecipient] = useState(invoice.company_snapshot?.billing_email || "");
    const [cc, setCc] = useState("");
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/invoices/${invoice.id}/send-email`,
                { recipient, cc, message });
            if (data?.configured === false) {
                setResult({ kind: "not_configured", msg: data.message });
                return;
            }
            toast.success(i18n.t("admin.l.Invoice_email_sent", "Invoice email sent"));
            onSent();
        } catch (e) {
            toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Send_failed", "Send failed")));
        } finally { setBusy(false); }
    };
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="invoice-send-dialog">
                <DialogHeader>
                    <DialogTitle>Send invoice {invoice.invoice_number}</DialogTitle>
                    <DialogDescription>
                        {i18n.t("admin.l.A_PDF_copy_will_be_attached_automatically", "A PDF copy will be attached automatically.")}
                    </DialogDescription>
                </DialogHeader>
                {result?.kind === "not_configured" && (
                    <div data-testid="invoice-send-notconfigured"
                         className="mb-3 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm p-3">
                        {result.msg}
                    </div>
                )}
                <div className="space-y-3">
                    <label className="block">
                        <span className="text-xs uppercase font-semibold text-neutral-600">{i18n.t("admin.l.Recipient", "Recipient")}</span>
                        <input data-testid="invoice-send-recipient"
                            value={recipient} onChange={(e) => setRecipient(e.target.value)}
                            className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                    </label>
                    <label className="block">
                        <span className="text-xs uppercase font-semibold text-neutral-600">{i18n.t("admin.l.CC_optional", "CC (optional)")}</span>
                        <input value={cc} onChange={(e) => setCc(e.target.value)}
                            className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                    </label>
                    <label className="block">
                        <span className="text-xs uppercase font-semibold text-neutral-600">{i18n.t("admin.l.Custom_message_optional", "Custom message (optional)")}</span>
                        <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                    </label>
                </div>
                <DialogFooter>
                    <button onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold">{i18n.t("admin.l.Cancel", "Cancel")}</button>
                    <button
                        data-testid="invoice-send-submit"
                        onClick={submit} disabled={busy || !recipient}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-60"
                    ><Mail className="h-4 w-4 inline mr-2" />{busy ? i18n.t("admin.l.Sending", "Sending…") : i18n.t("admin.l.Send", "Send")}</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─────────────────────────────────────────────────────────────
// Platform Billing Settings — editable identity for invoice PDFs.
// ─────────────────────────────────────────────────────────────
function PlatformBillingSettingsTab() {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/admin/platform-billing-settings").then(({ data }) => setForm(data));
    }, []);
    if (!form) return <div className="text-sm text-neutral-500">{i18n.t("admin.l.Loading", "Loading…")}</div>;
    const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const save = async () => {
        setBusy(true);
        try {
            const payload = { ...form };
            delete payload.id; delete payload.updated_at; delete payload._id;
            payload.payment_terms_days = Number(payload.payment_terms_days || 0);
            const { data } = await api.put("/admin/platform-billing-settings", payload);
            setForm(data);
            toast.success(i18n.t("admin.l.Billing_settings_saved", "Billing settings saved"));
        } catch (e) {
            toast.error(detailOr(e.response?.data?.detail, i18n.t("admin.l.Save_failed", "Save failed")));
        } finally { setBusy(false); }
    };
    const fields = [
        ["legal_company_name", i18n.t("admin.l.Legal_company_name", "Legal company name")],
        ["business_id", i18n.t("admin.l.Business_ID", "Business ID")], ["vat_id", i18n.t("admin.l.VAT_ID", "VAT ID")],
        ["address", i18n.t("admin.l.Street_address", "Street address")],
        ["postal_code", i18n.t("admin.l.Postal_code", "Postal code")], ["city", i18n.t("admin.l.City", "City")],
        ["country", i18n.t("admin.l.Country", "Country")],
        ["billing_email", i18n.t("admin.l.Billing_email", "Billing email")], ["support_email", i18n.t("admin.l.Support_email", "Support email")],
        ["iban", "IBAN"], ["bic_swift", "BIC / SWIFT"], ["bank_name", i18n.t("admin.l.Bank_name", "Bank name")],
        ["payment_terms_days", i18n.t("admin.l.Payment_terms_days", "Payment terms (days)")],
        ["website", i18n.t("admin.l.Website", "Website")], ["phone", i18n.t("admin.l.Phone", "Phone")],
        ["logo_ref", i18n.t("admin.l.Logo_reference", "Logo reference")],
    ];
    return (
        <div data-testid="admin-billing-settings" className="max-w-3xl">
            <div className="grid grid-cols-2 gap-3">
                {fields.map(([k, label]) => (
                    <label key={k} className="block">
                        <span className="text-xs uppercase font-semibold text-neutral-600">{label}</span>
                        <input
                            data-testid={`billing-setting-${k}`}
                            value={form[k] ?? ""}
                            onChange={(e) => setF(k, e.target.value)}
                            className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm"
                        />
                    </label>
                ))}
                <label className="block col-span-2">
                    <span className="text-xs uppercase font-semibold text-neutral-600">{i18n.t("admin.l.Invoice_footer_text", "Invoice footer text")}</span>
                    <textarea rows={2}
                        data-testid="billing-setting-invoice_footer"
                        value={form.invoice_footer || ""}
                        onChange={(e) => setF("invoice_footer", e.target.value)}
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                </label>
            </div>
            <div className="mt-4 flex justify-end">
                <button
                    data-testid="billing-setting-save"
                    onClick={save} disabled={busy}
                    className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-60"
                >{busy ? i18n.t("admin.l.Saving", "Saving…") : i18n.t("admin.l.Save", "Save")}</button>
            </div>
            <p className="mt-4 text-xs text-neutral-500 italic">
                {i18n.t("admin.l.These_values_are_read_every_time_an_invoice_PDF_is_generated_No_legal_", "These values are read every time an invoice PDF is generated. No legal or banking information is hardcoded anywhere in the codebase.")}
            </p>
        </div>
    );
}

